import SQLite, { Transaction, ResultSet, SQLError } from 'react-native-sqlite-storage';
import { Platform } from 'react-native';
import {
  ReviewTemplate,
  ReviewCategory,
  ReviewItem,
  Room,
  ReviewStatus,
} from '../types';
import {
  buildCacheMetadataSelectStatement,
  buildCacheMetadataUpsertStatement,
} from './offlineDatabase.cacheMetadataStatements';
import {
  openOfflineDatabase,
} from './offlineDatabase.connection';
import { bootstrapOfflineDatabase } from './offlineDatabase.bootstrap';
import { getCachedDataRow, mapCachedDataRows, stringifyCacheData } from './offlineDatabase.cacheRows';
import {
  buildCategoriesCacheClearStatement,
  buildCategoriesCacheSelectStatement,
  buildCategoryCacheInsertStatement,
  buildItemCacheInsertStatement,
  buildItemsCacheClearStatement,
  buildItemsCacheSelectStatement,
  buildRoomCacheInsertStatement,
  buildRoomsCacheClearDefaultStatement,
  buildRoomsCacheClearForPropertyStatement,
  buildRoomsCacheSelectStatement,
  buildTemplateCacheInsertStatement,
  buildTemplatesCacheDeleteByIdsStatement,
  buildTemplatesCacheFallbackSelectStatement,
  buildValidTemplatesCacheSelectStatement,
} from './offlineDatabase.cacheStatements';
import {
  mapRowToCompletedResponse,
  mapRowToIdMapping,
  mapRowToOfflineOnDemandItem,
  mapRowToPhotoMetadata,
  mapRowToPhotoQueueItem,
  mapRowToQuestionMapping,
  mapRowToResponse,
  mapRowToReview,
  mapRowToSharedItemLink,
} from './offlineDatabase.mappers';
import {
  buildOfflineItemSyncStatusUpdateStatement,
  buildOfflineOnDemandItemDeleteStatement,
  buildOfflineOnDemandItemInsertStatement,
  buildOfflineOnDemandItemsByReviewSelectStatement,
	  buildOfflineOnDemandItemSelectStatement,
	  buildOfflineOnDemandItemUpdateStatement,
	  buildPendingOfflineOnDemandItemsForEnvironmentSelectStatement,
	  buildPendingOfflineOnDemandItemsSelectStatement,
  type OfflineItemSyncStatus,
} from './offlineDatabase.onDemandStatements';
import {
  buildAllIdMappingsSelectStatement,
  buildIdMappingInsertStatement,
  buildOfflineRoomInsertStatement,
	  buildOfflineRoomsSelectStatement,
	  buildOfflineRoomSyncStatusUpdateStatement,
	  buildPendingOfflineRoomsForEnvironmentSelectStatement,
	  buildPendingOfflineRoomsSelectStatement,
  buildRealIdByTempIdSelectStatement,
  buildResponseRoomIdMappingUpdateStatement,
  buildTempIdByRealIdSelectStatement,
  type IdMappingEntityType,
  type OfflineEntitySyncStatus,
} from './offlineDatabase.offlineEntityStatements';
import {
  buildPhotoMetadataByResponseDeleteStatement,
  buildPhotoMetadataByResponseSelectStatement,
  buildPhotoMetadataDeleteStatement,
  buildPhotoMetadataUpsertStatement,
  buildPhotoQueueDeleteStatement,
  buildPhotoQueueInsertStatement,
  buildPhotoQueueSelectStatement,
} from './offlineDatabase.photoStatements';
import { getFirstResultRow, mapResultRows, resultRowsToArray } from './offlineDatabase.rows';
import {
  buildCachedReviewInsertStatement,
  buildRecentReviewAddressesSelectStatement,
  buildReviewAddressRowsSelectStatement,
  buildReviewByIdSelectStatement,
  buildReviewByTempIdSelectStatement,
  buildReviewDeleteStatement,
  buildReviewInsertStatement,
  buildReviewsByPropertySelectStatement,
  buildReviewsSelectStatement,
  buildReviewTemplateSnapshotDeleteStatement,
  buildReviewTemplateSnapshotSelectStatement,
  buildReviewTemplateSnapshotUpsertStatement,
  buildReviewUpdateStatement,
} from './offlineDatabase.reviewStatements';
import {
  buildCachedResponseUpsertStatement,
  buildResponseDeleteStatement,
  buildResponseInsertStatement,
  buildResponsesByReviewSelectStatement,
  buildResponseSelectStatement,
  buildResponsesBySyncStatusSelectStatement,
  buildResponsesByTempReviewSelectStatement,
} from './offlineDatabase.responseStatements';
import {
  OFFLINE_DATABASE_CLEAR_STATEMENTS,
} from './offlineDatabase.schema';
import {
  booleanToSqlValue,
  dateToSqlValue,
  jsonToSqlValue,
  nullableDateToSqlValue,
} from './offlineDatabase.serialization';
import {
  buildCompletedResponsesForItemSelectStatement,
  buildQuestionMappingsForLinkSelectStatement,
  buildSharedItemLinkCacheStatement,
  buildSharedLinksForItemsSelectStatement,
  buildSharedLinksForItemSelectStatement,
  buildSharedQuestionMappingCacheStatements,
} from './offlineDatabase.sharedStatements';
import {
  addDefinedSqlUpdate,
  addSqlUpdate,
  buildSqlUpdate,
  createSqlUpdateBuilder,
  hasSqlUpdateAssignments,
} from './offlineDatabase.sqlUpdates';
import type {
  CachedTemplate,
  OfflineMutationJournalEntry,
  RecoverableWorkItem,
  OfflineResponse,
  OfflineReview,
  PhotoQueueItem,
  ReviewRecoverySnapshot,
} from './offlineDatabase.types';
import {
  buildDatabaseStatsStatements,
  buildExpiredCacheDeleteStatements,
  buildRowCountStatement,
  buildStaleTemplatesCacheDeleteStatement,
  isOfflineDatabaseRowCountTable,
} from './offlineDatabase.utilityStatements';
import { legacyNegativeId, safeNegativeId } from '../utils/idUtils';
import {
  OFFLINE_LEGACY_ENVIRONMENT_KEY,
  getOfflineEnvironmentKey,
} from './offlineEnvironment';

export type {
  CachedTemplate,
  OfflineMutationJournalEntry,
  RecoverableWorkItem,
  OfflineResponse,
  OfflineReview,
  PhotoQueueItem,
  ReviewRecoverySnapshot,
} from './offlineDatabase.types';

class OfflineDatabaseService {
  public db: SQLite.SQLiteDatabase | null = null;
  public isInitialized = false;

  private getEnvironmentKey(): string {
    return getOfflineEnvironmentKey();
  }

  /**
   * Initialize the SQLite database and create tables
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    try {
      this.db = await openOfflineDatabase();

      await bootstrapOfflineDatabase(this.db);

      this.isInitialized = true;
    } catch (error) {
      console.error('[OfflineDB] ❌ FATAL ERROR initializing database:', {
        error: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined
      });
      throw new Error(`Failed to initialize offline database: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  // ====================
  // CACHE METADATA OPERATIONS
  // ====================

  /**
   * Update cache metadata
   */
  async updateCacheMetadata(key: string, lastUpdated: Date): Promise<void> {
    await this.initialize();

    try {
      const statement = buildCacheMetadataUpsertStatement(key, lastUpdated);
      await this.executeSql(statement.sql, statement.params);
    } catch (error) {
      console.error('[OfflineDatabase] Error updating cache metadata:', error);
    }
  }

  /**
   * Get cache metadata
   */
  async getCacheMetadata(key: string): Promise<{ lastUpdated: Date; expiresAt: Date } | null> {
    await this.initialize();

    try {
      const statement = buildCacheMetadataSelectStatement(key);
      const result = await this.executeSql(statement.sql, statement.params);

      if (result.rows.length === 0) {
        return null;
      }

      const row = result.rows.item(0);
      return {
        lastUpdated: new Date(row.last_updated),
        expiresAt: new Date(row.expires_at),
      };
    } catch (error) {
      console.error('[OfflineDatabase] Error getting cache metadata:', error);
      return null;
    }
  }

  /**
   * Clear all data
   */
  async clearAll(): Promise<void> {
    await this.initialize();

    try {
      const recoverableWorkCount = await this.getRecoverableWorkCount().catch(() => 0);
      if (recoverableWorkCount > 0) {
        const recoverableReviewIds = await this.getReviewIdsWithRecoverableWork().catch(() => []);
        for (const reviewId of recoverableReviewIds) {
          await this.createRecoverySnapshot(reviewId, 'clear_all_guard').catch((snapshotError) => {
            console.warn('[OfflineDatabase] Failed to snapshot recoverable work before clearAll:', snapshotError);
          });
        }

        console.warn('[OfflineDatabase] clearAll preserved recoverable offline work:', {
          recoverableWorkCount,
          recoverableReviewIds,
        });

        const cacheOnlyStatements = OFFLINE_DATABASE_CLEAR_STATEMENTS.filter(statement =>
          !/DELETE FROM (reviews|responses|photos_queue|offline_rooms|offline_on_demand_items|id_mappings)/.test(statement)
        );

        for (const statement of cacheOnlyStatements) {
          await this.executeSql(statement);
        }

        return;
      }

      for (const statement of OFFLINE_DATABASE_CLEAR_STATEMENTS) {
        await this.executeSql(statement);
      }
    } catch (error) {
      console.error('[OfflineDatabase] Error clearing all data:', error);
    }
  }

  /**
   * Generate a temporary ID for offline records
   */
  private generateTempId(): string {
    return `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private async resolveOfflineOnDemandItemLookupId(itemId: number | string): Promise<number | string> {
    if (typeof itemId !== 'number' || itemId >= 0) {
      return itemId;
    }

    const result = await this.executeSql(
      'SELECT id, temp_id FROM offline_on_demand_items'
    );

    for (let index = 0; index < result.rows.length; index++) {
      const row = result.rows.item(index);
      const tempId = row?.temp_id;
      if (
        typeof tempId === 'string' &&
        (safeNegativeId(tempId) === itemId || legacyNegativeId(tempId) === itemId)
      ) {
        return tempId;
      }
    }

    return itemId;
  }

  /**
   * Execute SQL with proper error handling
   */
  async executeSql(sql: string, params: any[] = []): Promise<ResultSet> {
    if (!this.db) {
      throw new Error('Database not initialized');
    }

    try {
      const result = await this.db.executeSql(sql, params);
      return result[0];
    } catch (error) {
      console.warn('[OfflineDB] SQL Error:', { sql, params, error });
      throw new Error(`SQL execution failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  // ====================
  // REVIEWS CRUD OPERATIONS
  // ====================

  /**
   * Create a review with a specific ID (for caching existing reviews)
   */
  async createReviewWithId(review: Partial<OfflineReview> & { id: number }): Promise<OfflineReview> {
    await this.initialize();

    const now = new Date().toISOString();
    const environmentKey = this.getEnvironmentKey();

    try {
      const existingReview = await this.getReview(review.id);
      const preserveLocalSyncStatus = existingReview &&
        (existingReview.syncStatus === 'pending' ||
          existingReview.syncStatus === 'failed' ||
          existingReview.syncStatus === 'syncing') &&
        (!review.syncStatus || review.syncStatus === 'synced');

      const syncStatus = preserveLocalSyncStatus
        ? existingReview.syncStatus
        : review.syncStatus || existingReview?.syncStatus || 'synced';

      if (existingReview) {
        const updateStatement = buildReviewUpdateStatement({
          id: review.id,
          environmentKey,
          tempId: review.tempId || existingReview.tempId || null,
          propertyId: review.propertyId ?? existingReview.propertyId,
          templateId: review.templateId ?? existingReview.templateId,
          reviewerId: review.reviewerId ?? existingReview.reviewerId ?? 0,
          status: review.status || existingReview.status || 'draft',
          startedAt: review.startedAt ? dateToSqlValue(review.startedAt) : dateToSqlValue(existingReview.startedAt),
          completedAt: review.completedAt !== undefined
            ? nullableDateToSqlValue(review.completedAt)
            : nullableDateToSqlValue(existingReview.completedAt),
          submittedAt: review.submittedAt !== undefined
            ? nullableDateToSqlValue(review.submittedAt)
            : nullableDateToSqlValue(existingReview.submittedAt),
          notes: review.notes !== undefined ? review.notes : existingReview.notes || null,
          onDemandProjects: review.onDemandProjects !== undefined ? review.onDemandProjects : existingReview.onDemandProjects ?? null,
          vendorNotes: review.vendorNotes !== undefined ? review.vendorNotes : existingReview.vendorNotes || null,
          address: review.address !== undefined ? review.address : existingReview.address || null,
          memberName: review.memberName !== undefined ? review.memberName : existingReview.memberName || null,
          memberEmail: review.memberEmail !== undefined ? review.memberEmail : existingReview.memberEmail || null,
          homeManagerName: review.homeManagerName !== undefined ? review.homeManagerName : existingReview.homeManagerName || null,
          homeManagerEmail: review.homeManagerEmail !== undefined ? review.homeManagerEmail : existingReview.homeManagerEmail || null,
          metadata: review.metadata !== undefined
            ? jsonToSqlValue(review.metadata)
            : jsonToSqlValue(existingReview.metadata),
          syncStatus,
          lastLocalMutationAt: existingReview.lastLocalMutationAt
            ? nullableDateToSqlValue(existingReview.lastLocalMutationAt)
            : null,
          lastServerSeenAt: now,
          localRevision: existingReview.localRevision ?? 0,
          serverRevision: (existingReview.serverRevision ?? 0) + 1,
          conflictStatus: preserveLocalSyncStatus
            ? existingReview.conflictStatus || 'local_wins'
            : review.conflictStatus || 'none',
          updatedAt: review.updatedAt ? dateToSqlValue(review.updatedAt) : now,
        });

        await this.executeSql(updateStatement.sql, updateStatement.params);

        return this.getReview(review.id);
      }

      // Use INSERT OR IGNORE for new cached reviews. Avoid INSERT OR REPLACE
      // because SQLite implements REPLACE as delete + insert, which can
      // cascade-delete pending response rows.
      const insertStatement = buildCachedReviewInsertStatement({
        id: review.id,
        environmentKey,
        tempId: review.tempId || null,
        propertyId: review.propertyId,
        templateId: review.templateId,
        reviewerId: review.reviewerId || 0,
        status: review.status || 'draft',
        startedAt: review.startedAt ? dateToSqlValue(review.startedAt) : now,
        completedAt: nullableDateToSqlValue(review.completedAt),
        submittedAt: nullableDateToSqlValue(review.submittedAt),
        notes: review.notes || null,
        onDemandProjects: review.onDemandProjects ?? null,
        vendorNotes: review.vendorNotes || null,
        address: review.address || null,
        memberName: review.memberName || null,
        memberEmail: review.memberEmail || null,
        homeManagerName: review.homeManagerName || null,
        homeManagerEmail: review.homeManagerEmail || null,
        metadata: jsonToSqlValue(review.metadata),
        syncStatus,
        lastLocalMutationAt: review.lastLocalMutationAt
          ? nullableDateToSqlValue(review.lastLocalMutationAt)
          : null,
        lastServerSeenAt: review.lastServerSeenAt
          ? nullableDateToSqlValue(review.lastServerSeenAt)
          : now,
        localRevision: review.localRevision ?? 0,
        serverRevision: review.serverRevision ?? 1,
        conflictStatus: review.conflictStatus || 'none',
        createdAt: review.createdAt ? dateToSqlValue(review.createdAt) : now,
        updatedAt: review.updatedAt ? dateToSqlValue(review.updatedAt) : now,
      });

      await this.executeSql(insertStatement.sql, insertStatement.params);

      return this.getReview(review.id);
    } catch (error) {
      console.error('[OfflineDatabase] Error upserting review with ID:', {
        reviewId: review.id,
        errorMessage: error?.message,
        errorCode: (error as any)?.code,
        sqliteCode: (error as any)?.sqliteCode,
        constraint: (error as any)?.constraint
      });
      throw error;
    }
  }

  /**
   * Create a new offline review
   */
  async createReview(review: Omit<OfflineReview, 'id' | 'tempId' | 'syncStatus' | 'createdAt' | 'updatedAt'>): Promise<OfflineReview> {
    await this.initialize();

    const tempId = this.generateTempId();
    const now = new Date().toISOString();
    const environmentKey = this.getEnvironmentKey();

    try {
      const insertStatement = buildReviewInsertStatement({
        environmentKey,
        tempId,
        propertyId: review.propertyId,
        templateId: review.templateId,
        reviewerId: review.reviewerId,
        status: review.status,
        startedAt: dateToSqlValue(review.startedAt),
        completedAt: nullableDateToSqlValue(review.completedAt),
        submittedAt: nullableDateToSqlValue(review.submittedAt),
        notes: review.notes || null,
        onDemandProjects: review.onDemandProjects ?? null,
        vendorNotes: review.vendorNotes || null,
        address: review.address || null,
        memberName: review.memberName || null,
        memberEmail: review.memberEmail || null,
        homeManagerName: review.homeManagerName || null,
        homeManagerEmail: review.homeManagerEmail || null,
        metadata: jsonToSqlValue(review.metadata),
        syncStatus: 'pending',
        lastLocalMutationAt: now,
        lastServerSeenAt: null,
        localRevision: 1,
        serverRevision: 0,
        conflictStatus: 'none',
        updatedAt: now,
      });

      const result = await this.executeSql(insertStatement.sql, insertStatement.params);

      const createdReview: OfflineReview = {
        ...review,
        id: result.insertId,
        tempId,
        environmentKey,
        syncStatus: 'pending',
        lastLocalMutationAt: new Date(now),
        lastServerSeenAt: null,
        localRevision: 1,
        serverRevision: 0,
        conflictStatus: 'none',
        createdAt: new Date(now),
        updatedAt: new Date(now),
      };

      return createdReview;
    } catch (error) {
      console.warn('[OfflineDB] Error creating review:', error);
      throw new Error(`Failed to create review: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get review by ID
   */
  async getReview(id: number): Promise<OfflineReview | null> {
    await this.initialize();

    try {
      // Try to get review by ID - SQLite automatically handles type conversion
      const selectStatement = buildReviewByIdSelectStatement(id, this.getEnvironmentKey());
      const result = await this.executeSql(selectStatement.sql, selectStatement.params);

      const row = getFirstResultRow(result);
      if (!row) {
        return null;
      }

      return mapRowToReview(row);
    } catch (error) {
      console.warn('[OfflineDB] Error getting review:', error);
      throw new Error(`Failed to get review: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get review by temporary ID
   */
  async getReviewByTempId(tempId: string): Promise<OfflineReview | null> {
    await this.initialize();

    try {
      const selectStatement = buildReviewByTempIdSelectStatement(tempId, this.getEnvironmentKey());
      const result = await this.executeSql(selectStatement.sql, selectStatement.params);

      const row = getFirstResultRow(result);
      if (!row) {
        return null;
      }

      return mapRowToReview(row);
    } catch (error) {
      console.warn('[OfflineDB] Error getting review by temp ID:', error);
      throw new Error(`Failed to get review by temp ID: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get all reviews with optional filtering
   */
  async getReviews(options: {
    status?: ReviewStatus;
    syncStatus?: 'pending' | 'syncing' | 'synced' | 'failed';
    limit?: number;
    offset?: number;
  } = {}): Promise<OfflineReview[]> {
    await this.initialize();

    const selectStatement = buildReviewsSelectStatement({
      ...options,
      environmentKey: this.getEnvironmentKey(),
    });

    try {
      const result = await this.executeSql(selectStatement.sql, selectStatement.params);
      return mapResultRows(result, mapRowToReview);
    } catch (error) {
      console.warn('[OfflineDB] Error getting reviews:', error);
      throw new Error(`Failed to get reviews: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get reviews for a specific property
   * Useful for address autocomplete and finding shared items
   *
   * @param propertyId - The property ID to filter by
   * @param options - Optional filters (status, limit)
   * @returns Array of reviews for the property, ordered by completedAt DESC, updatedAt DESC
   */
  async getReviewsByProperty(
    propertyId: number,
    options?: {
      status?: ReviewStatus;
      limit?: number;
    }
  ): Promise<OfflineReview[]> {
    await this.initialize();

    if (!this.db) {
      throw new Error('Database not initialized');
    }

    try {
      const selectStatement = buildReviewsByPropertySelectStatement(propertyId, {
        ...options,
        environmentKey: this.getEnvironmentKey(),
      });

      return new Promise((resolve, reject) => {
        this.db!.transaction((tx: Transaction) => {
          tx.executeSql(
            selectStatement.sql,
            selectStatement.params,
            (_: Transaction, result: ResultSet) => {
              resolve(mapResultRows(result, (row) => ({
                  id: row.id,
                  tempId: row.temp_id,
                  propertyId: row.property_id,
                  templateId: row.template_id,
                  status: row.status as ReviewStatus,
                  syncStatus: row.sync_status,
                  address: row.address,
                  vendorNotes: row.vendor_notes,
                  onDemandProjects: row.on_demand_projects,
                  completedAt: row.completed_at ? new Date(row.completed_at) : undefined,
                  submittedAt: row.submitted_at ? new Date(row.submitted_at) : undefined,
                  createdAt: new Date(row.created_at),
                  updatedAt: new Date(row.updated_at),
                })));
            },
            (_: Transaction, error: SQLError) => {
              console.warn('[OfflineDB] Error getting reviews by property:', error);
              reject(new Error(`Failed to get reviews by property: ${error.message}`));
              return false;
            }
          );
        });
      });
    } catch (error) {
      console.warn('[OfflineDB] Error getting reviews by property:', error);
      throw new Error(`Failed to get reviews by property: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get row count for a table
   * Useful for checking if tables are empty before running expensive queries
   *
   * @param tableName - The table to count rows in
   * @returns The number of rows in the table
   */
  async getRowCount(tableName: string): Promise<number> {
    await this.initialize();

    if (!this.db) {
      return 0;
    }

    try {
      if (!isOfflineDatabaseRowCountTable(tableName)) {
        console.warn(`[OfflineDB] Refusing row count for unknown table ${tableName}`);
        return 0;
      }

      const selectStatement = buildRowCountStatement(tableName);

      return new Promise((resolve, reject) => {
        this.db!.transaction((tx: Transaction) => {
          tx.executeSql(
            selectStatement.sql,
            selectStatement.params,
            (_: Transaction, result: ResultSet) => {
              const count = result.rows.item(0)?.count || 0;
              resolve(count);
            },
            (_: Transaction, error: SQLError) => {
              console.warn(`[OfflineDB] Error getting row count for ${tableName}:`, error);
              reject(new Error(`Failed to get row count: ${error.message}`));
              return false;
            }
          );
        });
      });
    } catch (error) {
      console.warn(`[OfflineDB] Error in getRowCount for ${tableName}:`, error);
      return 0; // Gracefully return 0 on error
    }
  }

  /**
   * Find property ID by normalized address match
   * More efficient than loading all reviews into JS for filtering
   *
   * @param address - The address to search for (will be normalized)
   * @returns The property ID if found, null otherwise
   */
  async getPropertyIdByNormalizedAddress(address: string): Promise<number | null> {
    await this.initialize();

    if (!this.db || !address) {
      return null;
    }

    try {
      // Normalize address for comparison: lowercase, trim, collapse spaces, remove punctuation
      const normalized = address.toLowerCase().trim()
        .replace(/\s+/g, ' ')
        .replace(/[.,#]/g, '');

      return new Promise((resolve, reject) => {
        this.db!.transaction((tx: Transaction) => {
	          const selectStatement = buildReviewAddressRowsSelectStatement(this.getEnvironmentKey());

          tx.executeSql(
            selectStatement.sql,
            selectStatement.params,
            (_: Transaction, result: ResultSet) => {
              // Filter in JS since SQLite doesn't have good string normalization functions
              for (let i = 0; i < result.rows.length; i++) {
                const row = result.rows.item(i);
                if (row.address) {
                  const cachedNormalized = row.address.toLowerCase().trim()
                    .replace(/\s+/g, ' ')
                    .replace(/[.,#]/g, '');

                  if (cachedNormalized === normalized) {
                    resolve(row.property_id);
                    return;
                  }
                }
              }

              // No match found
              resolve(null);
            },
            (_: Transaction, error: SQLError) => {
              console.warn('[OfflineDB] Error finding property by address:', error);
              reject(new Error(`Failed to find property by address: ${error.message}`));
              return false;
            }
          );
        });
      });
    } catch (error) {
      console.warn('[OfflineDB] Error in getPropertyIdByNormalizedAddress:', error);
      return null; // Gracefully return null on error
    }
  }

  /**
   * Get recent unique addresses from cached reviews
   * Returns distinct property/address pairs for offline autocomplete
   *
   * @param limit - Maximum number of addresses to return (default: 50)
   * @returns Array of {propertyId, address, lastUsed, status} ordered by completed first, then most recent
   */
  async getRecentAddresses(limit: number = 50): Promise<Array<{
    propertyId: number;
    address: string;
    lastUsed: Date;
    status: ReviewStatus;
  }>> {
    await this.initialize();

    if (!this.db) {
      throw new Error('Database not initialized');
    }

    try {
      return new Promise((resolve, reject) => {
        this.db!.transaction((tx: Transaction) => {
	          const selectStatement = buildRecentReviewAddressesSelectStatement(limit, this.getEnvironmentKey());

          tx.executeSql(
            selectStatement.sql,
            selectStatement.params,
            (_: Transaction, result: ResultSet) => {
              resolve(mapResultRows(result, (row) => ({
                  propertyId: row.property_id,
                  address: row.address,
                  lastUsed: new Date(row.last_used),
                  status: row.status as ReviewStatus
                })));
            },
            (_: Transaction, error: SQLError) => {
              console.warn('[OfflineDB] Error getting recent addresses:', error);
              reject(new Error(`Failed to get recent addresses: ${error.message}`));
              return false;
            }
          );
        });
      });
    } catch (error) {
      console.warn('[OfflineDB] Error getting recent addresses:', error);
      throw new Error(`Failed to get recent addresses: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Update a review
   */
  async updateReview(id: number, updates: Partial<OfflineReview>): Promise<void> {
    await this.initialize();

    const updateBuilder = createSqlUpdateBuilder();

    addDefinedSqlUpdate(updateBuilder, 'status', updates.status);
    addDefinedSqlUpdate(updateBuilder, 'completed_at', updates.completedAt, nullableDateToSqlValue);
    addDefinedSqlUpdate(updateBuilder, 'submitted_at', updates.submittedAt, nullableDateToSqlValue);
    addDefinedSqlUpdate(updateBuilder, 'notes', updates.notes);
    addDefinedSqlUpdate(updateBuilder, 'on_demand_projects', updates.onDemandProjects);
    addDefinedSqlUpdate(updateBuilder, 'vendor_notes', updates.vendorNotes);
    addDefinedSqlUpdate(updateBuilder, 'member_name', updates.memberName);
    addDefinedSqlUpdate(updateBuilder, 'member_email', updates.memberEmail);
    addDefinedSqlUpdate(updateBuilder, 'home_manager_name', updates.homeManagerName);
    addDefinedSqlUpdate(updateBuilder, 'home_manager_email', updates.homeManagerEmail);
    addDefinedSqlUpdate(updateBuilder, 'metadata', updates.metadata, jsonToSqlValue);
    addDefinedSqlUpdate(updateBuilder, 'sync_status', updates.syncStatus);
    addDefinedSqlUpdate(updateBuilder, 'last_sync_attempt', updates.lastSyncAttempt, nullableDateToSqlValue);
    addDefinedSqlUpdate(updateBuilder, 'sync_error', updates.syncError);
    addDefinedSqlUpdate(updateBuilder, 'last_local_mutation_at', updates.lastLocalMutationAt, nullableDateToSqlValue);
    addDefinedSqlUpdate(updateBuilder, 'last_server_seen_at', updates.lastServerSeenAt, nullableDateToSqlValue);
    addDefinedSqlUpdate(updateBuilder, 'local_revision', updates.localRevision);
    addDefinedSqlUpdate(updateBuilder, 'server_revision', updates.serverRevision);
    addDefinedSqlUpdate(updateBuilder, 'conflict_status', updates.conflictStatus);

    if (!hasSqlUpdateAssignments(updateBuilder)) {
      return; // Nothing to update
    }

    addSqlUpdate(updateBuilder, 'updated_at', new Date().toISOString());
    const updateStatement = buildSqlUpdate('reviews', updateBuilder, 'id = ? AND environment_key = ?', [id, this.getEnvironmentKey()]);

    try {
      await this.executeSql(updateStatement!.sql, updateStatement!.params);
    } catch (error) {
      console.warn('[OfflineDB] Error updating review:', error);
      throw new Error(`Failed to update review: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async updateReviewIfUnchanged(
    id: number,
    expectedLocalRevision: number,
    updates: Partial<OfflineReview>
  ): Promise<boolean> {
    await this.initialize();

    const updateBuilder = createSqlUpdateBuilder();

    addDefinedSqlUpdate(updateBuilder, 'status', updates.status);
    addDefinedSqlUpdate(updateBuilder, 'completed_at', updates.completedAt, nullableDateToSqlValue);
    addDefinedSqlUpdate(updateBuilder, 'submitted_at', updates.submittedAt, nullableDateToSqlValue);
    addDefinedSqlUpdate(updateBuilder, 'notes', updates.notes);
    addDefinedSqlUpdate(updateBuilder, 'on_demand_projects', updates.onDemandProjects);
    addDefinedSqlUpdate(updateBuilder, 'vendor_notes', updates.vendorNotes);
    addDefinedSqlUpdate(updateBuilder, 'member_name', updates.memberName);
    addDefinedSqlUpdate(updateBuilder, 'member_email', updates.memberEmail);
    addDefinedSqlUpdate(updateBuilder, 'home_manager_name', updates.homeManagerName);
    addDefinedSqlUpdate(updateBuilder, 'home_manager_email', updates.homeManagerEmail);
    addDefinedSqlUpdate(updateBuilder, 'metadata', updates.metadata, jsonToSqlValue);
    addDefinedSqlUpdate(updateBuilder, 'sync_status', updates.syncStatus);
    addDefinedSqlUpdate(updateBuilder, 'last_sync_attempt', updates.lastSyncAttempt, nullableDateToSqlValue);
    addDefinedSqlUpdate(updateBuilder, 'sync_error', updates.syncError);
    addDefinedSqlUpdate(updateBuilder, 'last_local_mutation_at', updates.lastLocalMutationAt, nullableDateToSqlValue);
    addDefinedSqlUpdate(updateBuilder, 'last_server_seen_at', updates.lastServerSeenAt, nullableDateToSqlValue);
    addDefinedSqlUpdate(updateBuilder, 'local_revision', updates.localRevision);
    addDefinedSqlUpdate(updateBuilder, 'server_revision', updates.serverRevision);
    addDefinedSqlUpdate(updateBuilder, 'conflict_status', updates.conflictStatus);

    if (!hasSqlUpdateAssignments(updateBuilder)) {
      return true;
    }

    addSqlUpdate(updateBuilder, 'updated_at', new Date().toISOString());
    const updateStatement = buildSqlUpdate(
      'reviews',
      updateBuilder,
	      'id = ? AND COALESCE(local_revision, 0) = ? AND environment_key = ?',
	      [id, expectedLocalRevision, this.getEnvironmentKey()]
    );

    try {
      const result = await this.executeSql(updateStatement!.sql, updateStatement!.params);
      return (result.rowsAffected ?? 0) > 0;
    } catch (error) {
      console.warn('[OfflineDB] Error conditionally updating review:', error);
      throw new Error(`Failed to conditionally update review: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Delete a review
   */
  async deleteReview(id: number): Promise<void> {
    await this.initialize();

    try {
	      await this.executeSql('DELETE FROM reviews WHERE id = ? AND environment_key = ?', [id, this.getEnvironmentKey()]);
    } catch (error) {
      console.warn('[OfflineDB] Error deleting review:', error);
      throw new Error(`Failed to delete review: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  // ====================
  // RESPONSES CRUD OPERATIONS
  // ====================

  /**
   * Create a response with a specific ID (for caching existing responses)
   */
  async createResponseWithId(response: Partial<OfflineResponse> & { id: number }): Promise<OfflineResponse> {
    await this.initialize();

    const now = new Date().toISOString();
    const environmentKey = this.getEnvironmentKey();

    try {
      const existingResponse = await this.getResponse(response.id);
      if (
        existingResponse &&
        (existingResponse.syncStatus === 'pending' ||
          existingResponse.syncStatus === 'failed' ||
          existingResponse.syncStatus === 'syncing')
      ) {
        return existingResponse;
      }

      const cachedResponseValues = {
        id: response.id,
        environmentKey,
        tempId: response.tempId || existingResponse?.tempId || null,
        reviewId: response.reviewId,
        tempReviewId: response.tempReviewId || existingResponse?.tempReviewId || null,
        itemId: response.itemId,
        roomId: response.roomId || null,
        floor: response.floor || null,
        isPresent: response.isPresent !== undefined ? booleanToSqlValue(response.isPresent) : 1,
        status: response.status || 'not_started',
        responseData: jsonToSqlValue(response.responseData),
        notes: response.notes || null,
        findings: jsonToSqlValue(response.findings || []),
        syncStatus: response.syncStatus || 'synced',
        itemSnapshot: jsonToSqlValue(response.itemSnapshot || existingResponse?.itemSnapshot),
        lastLocalMutationAt: existingResponse?.lastLocalMutationAt
          ? nullableDateToSqlValue(existingResponse.lastLocalMutationAt)
          : null,
        lastServerSeenAt: response.lastServerSeenAt
          ? nullableDateToSqlValue(response.lastServerSeenAt)
          : now,
        localRevision: existingResponse?.localRevision ?? 0,
        serverRevision: (existingResponse?.serverRevision ?? 0) + 1,
        conflictStatus: response.conflictStatus || 'none',
        createdAt: response.createdAt ? dateToSqlValue(response.createdAt) : now,
        updatedAt: response.updatedAt ? dateToSqlValue(response.updatedAt) : now,
      };

      if (existingResponse) {
        await this.executeSql(
          `UPDATE responses SET
	             environment_key = ?, temp_id = ?, review_id = ?, temp_review_id = ?, item_id = ?, room_id = ?, floor = ?,
	             is_present = ?, status = ?, response_data = ?, notes = ?, findings = ?, sync_status = ?,
             item_snapshot = ?, last_local_mutation_at = ?, last_server_seen_at = ?,
             local_revision = ?, server_revision = ?, conflict_status = ?, updated_at = ?
	           WHERE id = ? AND environment_key = ?`,
	          [
	            cachedResponseValues.environmentKey,
	            cachedResponseValues.tempId,
            cachedResponseValues.reviewId,
            cachedResponseValues.tempReviewId,
            cachedResponseValues.itemId,
            cachedResponseValues.roomId,
            cachedResponseValues.floor,
            cachedResponseValues.isPresent,
            cachedResponseValues.status,
            cachedResponseValues.responseData,
            cachedResponseValues.notes,
            cachedResponseValues.findings,
            cachedResponseValues.syncStatus,
            cachedResponseValues.itemSnapshot,
            cachedResponseValues.lastLocalMutationAt,
            cachedResponseValues.lastServerSeenAt,
            cachedResponseValues.localRevision,
            cachedResponseValues.serverRevision,
            cachedResponseValues.conflictStatus,
	            cachedResponseValues.updatedAt,
	            response.id,
	            environmentKey,
	          ]
	        );
      } else {
        const statement = buildCachedResponseUpsertStatement(cachedResponseValues);
        await this.executeSql(statement.sql, statement.params);
      }


      // Cache photo metadata if photos are provided
      if ((response as any).photos && Array.isArray((response as any).photos)) {
        for (const photo of (response as any).photos) {
          await this.cachePhotoMetadata({
            id: photo.id,
            responseId: photo.responseId,
            s3Key: photo.s3Key,
            s3Url: photo.s3Url,
            fileName: photo.fileName,
            mimeType: photo.mimeType,
            size: photo.size,
            caption: photo.caption,
            note: photo.note,
            metadata: photo.metadata,
            questionId: photo.questionId,
            uploadedAt: photo.uploadedAt,
          });
        }
      }

      return this.getResponse(response.id);
    } catch (error) {
      console.error('[OfflineDatabase] Error upserting response with ID:', {
        responseId: response.id,
        reviewId: response.reviewId,
        errorMessage: error?.message,
        errorCode: (error as any)?.code,
        sqliteCode: (error as any)?.sqliteCode
      });
      throw error;
    }
  }

  /**
   * Create a new offline response
   */
  async createResponse(response: Omit<OfflineResponse, 'id' | 'tempId' | 'syncStatus' | 'createdAt' | 'updatedAt'>): Promise<OfflineResponse> {
    await this.initialize();

    const tempId = this.generateTempId();
    const now = new Date().toISOString();
    const environmentKey = this.getEnvironmentKey();

    try {
      const statement = buildResponseInsertStatement({
        environmentKey,
        tempId,
        reviewId: response.reviewId,
        tempReviewId: response.tempReviewId || null,
        itemId: response.itemId,
        roomId: response.roomId || null,
        floor: response.floor || null,
        isPresent: booleanToSqlValue(response.isPresent),
        status: response.status,
        responseData: jsonToSqlValue(response.responseData),
        notes: response.notes || null,
        findings: jsonToSqlValue(response.findings || []),
        syncStatus: 'pending',
        itemSnapshot: jsonToSqlValue(response.itemSnapshot),
        lastLocalMutationAt: now,
        lastServerSeenAt: null,
        localRevision: 1,
        serverRevision: 0,
        conflictStatus: 'none',
        createdAt: now,
        updatedAt: now,
      });
      const result = await this.executeSql(statement.sql, statement.params);

      const createdResponse: OfflineResponse = {
        ...response,
        id: result.insertId,
        reviewId: response.reviewId, // Explicitly include reviewId
        tempId,
        environmentKey,
        syncStatus: 'pending',
        lastLocalMutationAt: new Date(now),
        lastServerSeenAt: null,
        localRevision: 1,
        serverRevision: 0,
        conflictStatus: 'none',
        createdAt: new Date(now),
        updatedAt: new Date(now),
      };

      return createdResponse;
    } catch (error) {
      console.warn('[OfflineDB] Error creating response:', error);
      throw new Error(`Failed to create response: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get response by ID
   */
  async getResponse(id: number): Promise<OfflineResponse | null> {
    await this.initialize();

    try {
      const statement = buildResponseSelectStatement(id, this.getEnvironmentKey());
      const result = await this.executeSql(statement.sql, statement.params);

      const row = getFirstResultRow(result);
      if (!row) {
        return null;
      }

      return mapRowToResponse(row);
    } catch (error) {
      console.warn('[OfflineDB] Error getting response:', error);
      throw new Error(`Failed to get response: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get responses by review ID
   */
  async getResponsesByReviewId(reviewId: number): Promise<OfflineResponse[]> {
    await this.initialize();

    try {
      const statement = buildResponsesByReviewSelectStatement(reviewId, this.getEnvironmentKey());
      const result = await this.executeSql(statement.sql, statement.params);

      return mapResultRows(result, mapRowToResponse);
    } catch (error) {
      console.warn('[OfflineDB] Error getting responses by review ID:', error);
      throw new Error(`Failed to get responses: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get responses by temporary review ID
   */
  async getResponsesByTempReviewId(tempReviewId: string): Promise<OfflineResponse[]> {
    await this.initialize();

    try {
      const statement = buildResponsesByTempReviewSelectStatement(tempReviewId, this.getEnvironmentKey());
      const result = await this.executeSql(statement.sql, statement.params);

      return mapResultRows(result, mapRowToResponse);
    } catch (error) {
      console.warn('[OfflineDB] Error getting responses by temp review ID:', error);
      throw new Error(`Failed to get responses: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get responses by sync status across all reviews.
   */
  async getResponsesBySyncStatus(
    statuses: Array<'pending' | 'syncing' | 'synced' | 'failed'>
  ): Promise<OfflineResponse[]> {
    await this.initialize();

    if (statuses.length === 0) {
      return [];
    }

    try {
      const statement = buildResponsesBySyncStatusSelectStatement(statuses, this.getEnvironmentKey());
      const result = await this.executeSql(statement.sql, statement.params);

      return mapResultRows(result, mapRowToResponse);
    } catch (error) {
      console.warn('[OfflineDB] Error getting responses by sync status:', error);
      throw new Error(`Failed to get responses by sync status: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Update a response
   */
  async updateResponse(id: number, updates: Partial<OfflineResponse>): Promise<void> {
    await this.initialize();

    const updateBuilder = createSqlUpdateBuilder();

    addDefinedSqlUpdate(updateBuilder, 'is_present', updates.isPresent, booleanToSqlValue);
    addDefinedSqlUpdate(updateBuilder, 'status', updates.status);
    addDefinedSqlUpdate(updateBuilder, 'response_data', updates.responseData, jsonToSqlValue);
    addDefinedSqlUpdate(updateBuilder, 'notes', updates.notes);
    addDefinedSqlUpdate(updateBuilder, 'findings', updates.findings, jsonToSqlValue);
    addDefinedSqlUpdate(updateBuilder, 'room_id', updates.roomId);
    addDefinedSqlUpdate(updateBuilder, 'floor', updates.floor);
    addDefinedSqlUpdate(updateBuilder, 'item_snapshot', updates.itemSnapshot, jsonToSqlValue);

    // Always mark as pending when updating, unless explicitly setting a different sync status
    if (updates.syncStatus !== undefined) {
      addSqlUpdate(updateBuilder, 'sync_status', updates.syncStatus);
    } else {
      // Auto-mark as pending when content is updated
      addSqlUpdate(updateBuilder, 'sync_status', 'pending');
    }

    addDefinedSqlUpdate(updateBuilder, 'last_sync_attempt', updates.lastSyncAttempt, nullableDateToSqlValue);
    addDefinedSqlUpdate(updateBuilder, 'sync_error', updates.syncError);
    addDefinedSqlUpdate(updateBuilder, 'last_local_mutation_at', updates.lastLocalMutationAt, nullableDateToSqlValue);
    addDefinedSqlUpdate(updateBuilder, 'last_server_seen_at', updates.lastServerSeenAt, nullableDateToSqlValue);
    addDefinedSqlUpdate(updateBuilder, 'local_revision', updates.localRevision);
    addDefinedSqlUpdate(updateBuilder, 'server_revision', updates.serverRevision);
    addDefinedSqlUpdate(updateBuilder, 'conflict_status', updates.conflictStatus);
    addSqlUpdate(updateBuilder, 'updated_at', new Date().toISOString());
    const updateStatement = buildSqlUpdate('responses', updateBuilder, 'id = ? AND environment_key = ?', [id, this.getEnvironmentKey()]);

    try {
      const result = await this.executeSql(updateStatement!.sql, updateStatement!.params);
      if (result.rowsAffected === 0) {
        throw new Error(`No response row updated for response ${id} in environment ${this.getEnvironmentKey()}`);
      }
    } catch (error) {
      console.warn('[OfflineDB] Error updating response:', error);
      throw new Error(`Failed to update response: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async updateResponseIfUnchanged(
    id: number,
    expectedLocalRevision: number,
    updates: Partial<OfflineResponse>
  ): Promise<boolean> {
    await this.initialize();

    const updateBuilder = createSqlUpdateBuilder();

    addDefinedSqlUpdate(updateBuilder, 'is_present', updates.isPresent, booleanToSqlValue);
    addDefinedSqlUpdate(updateBuilder, 'status', updates.status);
    addDefinedSqlUpdate(updateBuilder, 'response_data', updates.responseData, jsonToSqlValue);
    addDefinedSqlUpdate(updateBuilder, 'notes', updates.notes);
    addDefinedSqlUpdate(updateBuilder, 'findings', updates.findings, jsonToSqlValue);
    addDefinedSqlUpdate(updateBuilder, 'room_id', updates.roomId);
    addDefinedSqlUpdate(updateBuilder, 'floor', updates.floor);
    addDefinedSqlUpdate(updateBuilder, 'item_snapshot', updates.itemSnapshot, jsonToSqlValue);
    addDefinedSqlUpdate(updateBuilder, 'sync_status', updates.syncStatus);
    addDefinedSqlUpdate(updateBuilder, 'last_sync_attempt', updates.lastSyncAttempt, nullableDateToSqlValue);
    addDefinedSqlUpdate(updateBuilder, 'sync_error', updates.syncError);
    addDefinedSqlUpdate(updateBuilder, 'last_local_mutation_at', updates.lastLocalMutationAt, nullableDateToSqlValue);
    addDefinedSqlUpdate(updateBuilder, 'last_server_seen_at', updates.lastServerSeenAt, nullableDateToSqlValue);
    addDefinedSqlUpdate(updateBuilder, 'local_revision', updates.localRevision);
    addDefinedSqlUpdate(updateBuilder, 'server_revision', updates.serverRevision);
    addDefinedSqlUpdate(updateBuilder, 'conflict_status', updates.conflictStatus);

    if (!hasSqlUpdateAssignments(updateBuilder)) {
      return true;
    }

    addSqlUpdate(updateBuilder, 'updated_at', new Date().toISOString());
    const updateStatement = buildSqlUpdate(
      'responses',
      updateBuilder,
	      'id = ? AND COALESCE(local_revision, 0) = ? AND environment_key = ?',
	      [id, expectedLocalRevision, this.getEnvironmentKey()]
    );

    try {
      const result = await this.executeSql(updateStatement!.sql, updateStatement!.params);
      return (result.rowsAffected ?? 0) > 0;
    } catch (error) {
      console.warn('[OfflineDB] Error conditionally updating response:', error);
      throw new Error(`Failed to conditionally update response: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Delete a response
   */
  async deleteResponse(id: number): Promise<void> {
    await this.initialize();

    try {
	      await this.executeSql('DELETE FROM responses WHERE id = ? AND environment_key = ?', [id, this.getEnvironmentKey()]);
    } catch (error) {
      console.warn('[OfflineDB] Error deleting response:', error);
      throw new Error(`Failed to delete response: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  // ====================
  // PHOTOS QUEUE CRUD OPERATIONS
  // ====================

  /**
   * Add photo to upload queue
   */
  async addPhotoToQueue(photo: Omit<PhotoQueueItem, 'id' | 'createdAt'>): Promise<PhotoQueueItem> {
    await this.initialize();

    const now = new Date().toISOString();
    const environmentKey = this.getEnvironmentKey();

    try {
      const statement = buildPhotoQueueInsertStatement({
        environmentKey,
        localPath: photo.localPath,
        reviewResponseId: photo.reviewResponseId || null,
        tempResponseId: photo.tempId || null,
        fileName: photo.fileName,
        mimeType: photo.mimeType,
        size: photo.size,
        caption: photo.caption || null,
        note: photo.note ?? null,
        metadata: jsonToSqlValue(photo.metadata),
        status: photo.status,
        retryCount: photo.retryCount,
        errorMessage: photo.errorMessage || null,
        priority: photo.priority || 'normal',
        isHandwriting: booleanToSqlValue(Boolean(photo.isHandwriting)),
        presignedUrlExpiry: nullableDateToSqlValue(photo.presignedUrlExpiry),
        processedForVision: booleanToSqlValue(Boolean(photo.processedForVision)),
        createdAt: now,
        lastAttemptAt: nullableDateToSqlValue(photo.lastAttemptAt),
      });
      const result = await this.executeSql(statement.sql, statement.params);

      const createdPhoto: PhotoQueueItem = {
        ...photo,
        id: result.insertId,
        environmentKey,
        createdAt: new Date(now),
      };

      return createdPhoto;
    } catch (error) {
      console.warn('[OfflineDB] Error adding photo to queue:', error);
      throw new Error(`Failed to add photo to queue: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get photos from queue with optional filtering
   */
  async getPhotosFromQueue(options: {
    status?: 'pending' | 'uploading' | 'uploaded' | 'failed';
    limit?: number;
  } = {}): Promise<PhotoQueueItem[]> {
    await this.initialize();

    try {
      const statement = buildPhotoQueueSelectStatement({
        ...options,
        environmentKey: this.getEnvironmentKey(),
      });
      const result = await this.executeSql(statement.sql, statement.params);
      return mapResultRows(result, mapRowToPhotoQueueItem);
    } catch (error) {
      console.warn('[OfflineDB] Error getting photos from queue:', error);
      throw new Error(`Failed to get photos from queue: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Update photo queue item
   */
  async updatePhotoQueueItem(id: number, updates: Partial<PhotoQueueItem>): Promise<void> {
    await this.initialize();

    const updateBuilder = createSqlUpdateBuilder();

    addDefinedSqlUpdate(updateBuilder, 'local_path', updates.localPath);
    addDefinedSqlUpdate(updateBuilder, 'review_response_id', updates.reviewResponseId);
    addDefinedSqlUpdate(updateBuilder, 'temp_response_id', updates.tempId);
    addDefinedSqlUpdate(updateBuilder, 'size', updates.size);
    addDefinedSqlUpdate(updateBuilder, 'status', updates.status);
    addDefinedSqlUpdate(updateBuilder, 'retry_count', updates.retryCount);
    addDefinedSqlUpdate(updateBuilder, 'error_message', updates.errorMessage);
    addDefinedSqlUpdate(updateBuilder, 'last_attempt_at', updates.lastAttemptAt, nullableDateToSqlValue);
    addDefinedSqlUpdate(updateBuilder, 'priority', updates.priority);
    addDefinedSqlUpdate(updateBuilder, 'is_handwriting', updates.isHandwriting, booleanToSqlValue);
    addDefinedSqlUpdate(updateBuilder, 'presigned_url_expiry', updates.presignedUrlExpiry, nullableDateToSqlValue);
    addDefinedSqlUpdate(updateBuilder, 'processed_for_vision', updates.processedForVision, booleanToSqlValue);
    addDefinedSqlUpdate(updateBuilder, 'note', updates.note);
    addDefinedSqlUpdate(updateBuilder, 'metadata', updates.metadata, jsonToSqlValue);

    if (!hasSqlUpdateAssignments(updateBuilder)) {
      return; // Nothing to update
    }

    const updateStatement = buildSqlUpdate('photos_queue', updateBuilder, 'id = ? AND environment_key = ?', [id, this.getEnvironmentKey()]);

    try {
      await this.executeSql(updateStatement!.sql, updateStatement!.params);
    } catch (error) {
      console.warn('[OfflineDB] Error updating photo queue item:', error);
      throw new Error(`Failed to update photo queue item: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Remove photo from queue
   */
  async removePhotoFromQueue(id: number): Promise<void> {
    await this.initialize();

    try {
      await this.executeSql('DELETE FROM photos_queue WHERE id = ? AND environment_key = ?', [id, this.getEnvironmentKey()]);
    } catch (error) {
      console.warn('[OfflineDB] Error removing photo from queue:', error);
      throw new Error(`Failed to remove photo from queue: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  // ====================
  // PHOTO METADATA OPERATIONS
  // ====================

  /**
   * Cache photo metadata for an uploaded photo
   */
  async cachePhotoMetadata(photo: {
    id: number;
    responseId: number;
    s3Key: string;
    s3Url?: string;
    fileName: string;
    mimeType: string;
    size?: number;
    caption?: string;
    note?: string | null;
    noteSyncStatus?: 'pending' | 'syncing' | 'synced' | 'failed';
    noteSyncError?: string | null;
    metadata?: string;
    questionId?: string;
    uploadedAt?: Date | string;
  }): Promise<void> {
    await this.initialize();

    try {
      const existing = await this.getPhotoMetadataById(photo.id);
      const preservePendingNote = existing?.noteSyncStatus === 'pending' ||
        existing?.noteSyncStatus === 'syncing' ||
        existing?.noteSyncStatus === 'failed';
      const statement = buildPhotoMetadataUpsertStatement({
        id: photo.id,
        responseId: photo.responseId,
        s3Key: photo.s3Key,
        s3Url: photo.s3Url || null,
        fileName: photo.fileName,
        mimeType: photo.mimeType,
        size: photo.size || null,
        caption: photo.caption || null,
        note: preservePendingNote
          ? existing.note ?? null
          : photo.note !== undefined
            ? photo.note
            : existing?.note ?? null,
        noteSyncStatus: preservePendingNote
          ? existing.noteSyncStatus
          : photo.noteSyncStatus || 'synced',
        noteSyncError: preservePendingNote
          ? existing.noteSyncError ?? null
          : photo.noteSyncError ?? null,
        metadata: photo.metadata || null,
        questionId: photo.questionId || null,
        uploadedAt: nullableDateToSqlValue(photo.uploadedAt),
        cachedAt: new Date().toISOString(),
      });
      await this.executeSql(statement.sql, statement.params);
    } catch (error) {
      console.error('[OfflineDB] Error caching photo metadata:', error);
      throw error;
    }
  }

  /**
   * Get photo metadata for a response
   */
  async getPhotoMetadataByResponseId(responseId: number): Promise<any[]> {
    await this.initialize();

    try {
      const statement = buildPhotoMetadataByResponseSelectStatement(responseId);
      const results = await this.executeSql(statement.sql, statement.params);

      return mapResultRows(results, mapRowToPhotoMetadata);
    } catch (error) {
      console.error('[OfflineDB] Error getting photo metadata:', error);
      return [];
    }
  }

  async getPhotoMetadataById(photoId: number): Promise<any | null> {
    await this.initialize();

    try {
      const result = await this.executeSql(
        'SELECT * FROM photos_metadata WHERE id = ?',
        [photoId]
      );
      const row = getFirstResultRow(result);
      return row ? mapRowToPhotoMetadata(row) : null;
    } catch (error) {
      console.error('[OfflineDB] Error getting photo metadata by ID:', error);
      return null;
    }
  }

  async updatePhotoMetadataNote(
    photoId: number,
    note: string,
    noteSyncStatus: 'pending' | 'syncing' | 'synced' | 'failed' = 'pending',
    noteSyncError: string | null = null
  ): Promise<void> {
    await this.initialize();
    await this.executeSql(
      `UPDATE photos_metadata
       SET note = ?, note_sync_status = ?, note_sync_error = ?, cached_at = ?
       WHERE id = ?`,
      [note, noteSyncStatus, noteSyncError, new Date().toISOString(), photoId]
    );
  }

  async getPendingPhotoNotes(): Promise<Array<{ id: number; note: string }>> {
    await this.initialize();
    const result = await this.executeSql(
      `SELECT id, note FROM photos_metadata
       WHERE note_sync_status IN ('pending', 'failed')
       ORDER BY cached_at ASC`
    );
    return mapResultRows(result, row => ({
      id: Number(row.id),
      note: typeof row.note === 'string' ? row.note : '',
    }));
  }

  /**
   * Delete photo metadata by ID
   */
  async deletePhotoMetadata(photoId: number): Promise<void> {
    await this.initialize();

    try {
      const statement = buildPhotoMetadataDeleteStatement(photoId);
      await this.executeSql(statement.sql, statement.params);
    } catch (error) {
      console.error('[OfflineDB] Error deleting photo metadata:', error);
      throw error;
    }
  }

  /**
   * Delete all photo metadata for a response
   */
  async deletePhotoMetadataByResponseId(responseId: number): Promise<void> {
    await this.initialize();

    try {
      const statement = buildPhotoMetadataByResponseDeleteStatement(responseId);
      await this.executeSql(statement.sql, statement.params);
    } catch (error) {
      console.error('[OfflineDB] Error deleting photo metadata for response:', error);
      throw error;
    }
  }

  // ====================
  // OFFLINE RECOVERY OPERATIONS
  // ====================

  async recordOfflineMutation(entry: OfflineMutationJournalEntry): Promise<number | undefined> {
    await this.initialize();

	    const now = new Date().toISOString();
	    const environmentKey = entry.environmentKey || this.getEnvironmentKey();

	    try {
	      const result = await this.executeSql(
	        `INSERT INTO offline_mutation_journal (
	          environment_key, entity_type, entity_id, review_id, item_id, mutation_type, payload,
	          previous_snapshot, sync_result, status, tombstone, created_at, updated_at
	        ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
	        [
	          environmentKey,
	          entry.entityType,
          entry.entityId ?? null,
          entry.reviewId ?? null,
          entry.itemId ?? null,
          entry.mutationType,
          jsonToSqlValue(entry.payload),
          jsonToSqlValue(entry.previousSnapshot),
          jsonToSqlValue(entry.syncResult),
          entry.status || 'pending',
          booleanToSqlValue(Boolean(entry.tombstone)),
          entry.createdAt ? dateToSqlValue(entry.createdAt) : now,
          entry.updatedAt ? dateToSqlValue(entry.updatedAt) : now,
        ]
      );

      return result.insertId;
    } catch (error) {
      console.warn('[OfflineDatabase] Failed to record offline mutation:', error);
      return undefined;
    }
  }

  async updateOfflineMutationStatus(
    id: number,
    status: OfflineMutationJournalEntry['status'],
    syncResult?: unknown
  ): Promise<void> {
    await this.initialize();

    try {
      await this.executeSql(
	        `UPDATE offline_mutation_journal
	         SET status = ?, sync_result = ?, updated_at = ?
	         WHERE id = ? AND environment_key = ?`,
	        [status || 'pending', jsonToSqlValue(syncResult), new Date().toISOString(), id, this.getEnvironmentKey()]
	      );
    } catch (error) {
      console.warn('[OfflineDatabase] Failed to update offline mutation status:', error);
    }
  }

  async markOfflineMutationsSyncedForResponse(reviewId: number, itemId?: number | null): Promise<void> {
    await this.initialize();

    try {
      if (itemId == null) {
        await this.executeSql(
          `UPDATE offline_mutation_journal
           SET status = 'synced', updated_at = ?
	           WHERE entity_type = 'response' AND review_id = ? AND environment_key = ? AND status IN ('pending', 'failed', 'syncing', 'conflict')`,
	          [new Date().toISOString(), reviewId, this.getEnvironmentKey()]
        );
        return;
      }

      await this.executeSql(
        `UPDATE offline_mutation_journal
         SET status = 'synced', updated_at = ?
	         WHERE entity_type = 'response' AND review_id = ? AND item_id = ? AND environment_key = ?
	           AND status IN ('pending', 'failed', 'syncing', 'conflict')`,
	        [new Date().toISOString(), reviewId, itemId, this.getEnvironmentKey()]
      );
    } catch (error) {
      console.warn('[OfflineDatabase] Failed to mark response mutations synced:', error);
    }
  }

  async markOfflineMutationsSyncedForReview(reviewId: number): Promise<void> {
    await this.initialize();

    try {
      await this.executeSql(
        `UPDATE offline_mutation_journal
         SET status = 'synced', updated_at = ?
	         WHERE entity_type = 'review'
	           AND (review_id = ? OR entity_id = ?)
	          AND environment_key = ?
	           AND status IN ('pending', 'failed', 'syncing', 'conflict')`,
	        [new Date().toISOString(), reviewId, reviewId, this.getEnvironmentKey()]
      );
    } catch (error) {
      console.warn('[OfflineDatabase] Failed to mark review mutations synced:', error);
    }
  }

  async createRecoverySnapshot(reviewId: number, reason: string): Promise<void> {
    await this.initialize();

    try {
      const review = await this.getReview(reviewId);
      const responses = await this.getResponsesByReviewId(reviewId).catch(() => []);
      const photoQueue = await this.getPhotosFromQueue({}).then(photos =>
        photos.filter(photo => {
          if (photo.metadata?.reviewId === reviewId) {
            return true;
          }

          return responses.some(response =>
            photo.reviewResponseId === response.id ||
            Boolean(response.tempId && photo.tempId === response.tempId)
          );
        })
      ).catch(() => []);
      const photoMetadata: any[] = [];

      for (const response of responses) {
        const metadata = await this.getPhotoMetadataByResponseId(response.id).catch(() => []);
        photoMetadata.push(...metadata);
      }

      const snapshot: ReviewRecoverySnapshot['snapshot'] = {
        review,
        responses,
        photoQueue,
        photoMetadata,
      };

      await this.executeSql(
	        `INSERT INTO review_recovery_snapshots (environment_key, review_id, reason, snapshot, created_at)
	         VALUES (?, ?, ?, ?, ?)`,
	        [this.getEnvironmentKey(), reviewId, reason, JSON.stringify(snapshot), new Date().toISOString()]
      );
    } catch (error) {
      console.warn('[OfflineDatabase] Failed to create recovery snapshot:', {
        reviewId,
        reason,
        error,
      });
    }
  }

  async getLatestRecoverySnapshot(reviewId: number): Promise<ReviewRecoverySnapshot | null> {
    await this.initialize();

    try {
      const result = await this.executeSql(
        `SELECT * FROM review_recovery_snapshots
	         WHERE review_id = ? AND environment_key = ?
	         ORDER BY created_at DESC, id DESC
	         LIMIT 1`,
	        [reviewId, this.getEnvironmentKey()]
      );

      const row = getFirstResultRow(result);
      if (!row) {
        return null;
      }

      return {
	        id: row.id,
	        environmentKey: row.environment_key,
	        reviewId: row.review_id,
        reason: row.reason,
        snapshot: JSON.parse(row.snapshot),
        createdAt: new Date(row.created_at),
      };
    } catch (error) {
      console.warn('[OfflineDatabase] Failed to load recovery snapshot:', error);
      return null;
    }
  }

  async restoreLatestRecoverySnapshot(reviewId: number): Promise<boolean> {
    await this.initialize();

    const snapshot = await this.getLatestRecoverySnapshot(reviewId);
    if (!snapshot) {
      return false;
    }

    try {
      const { review, responses } = snapshot.snapshot;

      if (review) {
        const existingReview = await this.getReview(review.id).catch(() => null);
        if (existingReview) {
          await this.updateReview(review.id, {
            status: review.status,
            completedAt: review.completedAt,
            submittedAt: review.submittedAt,
            notes: review.notes,
            onDemandProjects: review.onDemandProjects,
            vendorNotes: review.vendorNotes,
            memberName: review.memberName,
            memberEmail: review.memberEmail,
            homeManagerName: review.homeManagerName,
            homeManagerEmail: review.homeManagerEmail,
            metadata: review.metadata,
            syncStatus: review.syncStatus === 'synced' ? 'pending' : review.syncStatus,
            lastLocalMutationAt: review.lastLocalMutationAt || new Date(),
            lastServerSeenAt: review.lastServerSeenAt,
            localRevision: (review.localRevision ?? 0) + 1,
            serverRevision: review.serverRevision ?? 0,
            conflictStatus: 'restored',
          });
        } else {
          await this.createReviewWithId({
            ...review,
            syncStatus: review.syncStatus === 'synced' ? 'pending' : review.syncStatus,
            conflictStatus: 'restored',
            lastLocalMutationAt: review.lastLocalMutationAt || new Date(),
          });
        }
      }

      for (const response of responses) {
        const existingResponse = await this.getResponse(response.id).catch(() => null);
        const restoredSyncStatus = response.syncStatus === 'synced' ? 'pending' : response.syncStatus;
        const restoredResponse = {
          ...response,
          syncStatus: restoredSyncStatus,
          lastLocalMutationAt: response.lastLocalMutationAt || new Date(),
          localRevision: (response.localRevision ?? 0) + 1,
          conflictStatus: 'restored' as const,
        };

        if (existingResponse) {
          await this.updateResponse(response.id, restoredResponse);
        } else {
          await this.createResponseWithId(restoredResponse);
        }
      }

      await this.recordOfflineMutation({
        entityType: 'review',
        entityId: reviewId,
        reviewId,
        mutationType: 'restore_recovery_snapshot',
        payload: { snapshotId: snapshot.id, reason: snapshot.reason },
        status: 'pending',
      });

      console.warn('[OfflineDatabase] Restored latest recovery snapshot:', {
        reviewId,
        snapshotId: snapshot.id,
        reason: snapshot.reason,
      });
      return true;
    } catch (error) {
      console.warn('[OfflineDatabase] Failed to restore recovery snapshot:', error);
      return false;
    }
  }

  async exportRecoveryBundle(reviewId?: number): Promise<Record<string, unknown>> {
    await this.initialize();

	    const environmentKey = this.getEnvironmentKey();
	    const reviewFilter = reviewId ? 'WHERE review_id = ? AND environment_key = ?' : 'WHERE environment_key = ?';
	    const params = reviewId ? [reviewId, environmentKey] : [environmentKey];

    const journalResult = await this.executeSql(
      `SELECT * FROM offline_mutation_journal ${reviewFilter} ORDER BY created_at ASC`,
      params
    );
    const snapshotsResult = await this.executeSql(
      `SELECT * FROM review_recovery_snapshots ${reviewFilter} ORDER BY created_at ASC`,
      params
    );

    return {
      exportedAt: new Date().toISOString(),
      reviewId: reviewId ?? null,
      journal: resultRowsToArray(journalResult),
      snapshots: resultRowsToArray(snapshotsResult),
    };
  }

  async getRecoverableWorkCount(reviewId?: number): Promise<number> {
    const items = await this.getRecoverableWorkItems(reviewId);
    return items.length;
  }

  async getRecoverableWorkItems(reviewId?: number): Promise<RecoverableWorkItem[]> {
    return this.getRecoverableWorkItemsForEnvironment(this.getEnvironmentKey(), reviewId);
  }

  async getLegacyRecoverableWorkItems(reviewId?: number): Promise<RecoverableWorkItem[]> {
    return this.getRecoverableWorkItemsForEnvironment(OFFLINE_LEGACY_ENVIRONMENT_KEY, reviewId);
  }

  async adoptLegacyRecoveryEnvironmentForReview(reviewId: number): Promise<void> {
    await this.initialize();

    const targetEnvironmentKey = this.getEnvironmentKey();
    if (targetEnvironmentKey === OFFLINE_LEGACY_ENVIRONMENT_KEY) {
      return;
    }

    const legacyReviewResult = await this.executeSql(
      'SELECT temp_id FROM reviews WHERE id = ? AND environment_key = ? LIMIT 1',
      [reviewId, OFFLINE_LEGACY_ENVIRONMENT_KEY]
    );
    const legacyReview = getFirstResultRow<{ temp_id?: string | null }>(legacyReviewResult);
    const responseResult = await this.executeSql(
      'SELECT id, temp_id FROM responses WHERE review_id = ? AND environment_key = ?',
      [reviewId, OFFLINE_LEGACY_ENVIRONMENT_KEY]
    );
    const responseRows = resultRowsToArray(responseResult) as Array<{ id?: number | null; temp_id?: string | null }>;
    const onDemandResult = await this.executeSql(
      'SELECT temp_id FROM offline_on_demand_items WHERE review_id = ? AND environment_key = ?',
      [reviewId, OFFLINE_LEGACY_ENVIRONMENT_KEY]
    );
    const onDemandRows = resultRowsToArray(onDemandResult) as Array<{ temp_id?: string | null }>;

    await this.executeSql(
      'UPDATE reviews SET environment_key = ? WHERE id = ? AND environment_key = ?',
      [targetEnvironmentKey, reviewId, OFFLINE_LEGACY_ENVIRONMENT_KEY]
    );
    await this.executeSql(
      'UPDATE responses SET environment_key = ? WHERE review_id = ? AND environment_key = ?',
      [targetEnvironmentKey, reviewId, OFFLINE_LEGACY_ENVIRONMENT_KEY]
    );
    await this.executeSql(
      'UPDATE offline_on_demand_items SET environment_key = ? WHERE review_id = ? AND environment_key = ?',
      [targetEnvironmentKey, reviewId, OFFLINE_LEGACY_ENVIRONMENT_KEY]
    );
    await this.executeSql(
      'UPDATE offline_mutation_journal SET environment_key = ? WHERE review_id = ? AND environment_key = ?',
      [targetEnvironmentKey, reviewId, OFFLINE_LEGACY_ENVIRONMENT_KEY]
    );
    await this.executeSql(
      'UPDATE review_recovery_snapshots SET environment_key = ? WHERE review_id = ? AND environment_key = ?',
      [targetEnvironmentKey, reviewId, OFFLINE_LEGACY_ENVIRONMENT_KEY]
    );

    const tempIds = [
      legacyReview?.temp_id,
      ...responseRows.map(row => row.temp_id),
      ...onDemandRows.map(row => row.temp_id),
    ].filter((tempId): tempId is string => typeof tempId === 'string' && tempId.length > 0);

    for (const tempId of tempIds) {
      await this.executeSql(
        'UPDATE id_mappings SET environment_key = ? WHERE temp_id = ? AND environment_key = ?',
        [targetEnvironmentKey, tempId, OFFLINE_LEGACY_ENVIRONMENT_KEY]
      );
    }

    const responseIds = new Set(
      responseRows
        .map(row => row.id)
        .filter((id): id is number => typeof id === 'number' && Number.isFinite(id))
    );
    const responseTempIds = new Set(responseRows.map(row => row.temp_id).filter(Boolean));
    const legacyPhotosResult = await this.executeSql(
      'SELECT id, review_response_id, temp_response_id, metadata FROM photos_queue WHERE environment_key = ?',
      [OFFLINE_LEGACY_ENVIRONMENT_KEY]
    );
    const legacyPhotos = resultRowsToArray(legacyPhotosResult) as Array<{
      id?: number | null;
      review_response_id?: number | null;
      temp_response_id?: string | null;
      metadata?: string | null;
    }>;

    for (const photo of legacyPhotos) {
      let metadata: any = null;
      if (typeof photo.metadata === 'string' && photo.metadata.length > 0) {
        try {
          metadata = JSON.parse(photo.metadata);
        } catch {
          metadata = null;
        }
      }
      const photoReviewId = Number(metadata?.reviewId);
      const belongsToReview =
        photoReviewId === reviewId ||
        (typeof photo.review_response_id === 'number' && responseIds.has(photo.review_response_id)) ||
        (typeof photo.temp_response_id === 'string' && responseTempIds.has(photo.temp_response_id));

      if (belongsToReview && photo.id != null) {
        await this.executeSql(
          'UPDATE photos_queue SET environment_key = ? WHERE id = ? AND environment_key = ?',
          [targetEnvironmentKey, photo.id, OFFLINE_LEGACY_ENVIRONMENT_KEY]
        );
      }
    }
  }

  private async getRecoverableWorkItemsForEnvironment(
    environmentKey: string,
    reviewId?: number
  ): Promise<RecoverableWorkItem[]> {
    await this.initialize();

    const items: RecoverableWorkItem[] = [];
    const reviewParams = reviewId ? [environmentKey, reviewId] : [environmentKey];

	    const reviewResult = await this.executeSql(
	      `SELECT * FROM reviews
	       WHERE environment_key = ?
	         AND sync_status NOT IN ('pending', 'syncing')
	         AND conflict_status IN ('local_wins', 'conflicted', 'restored')${reviewId ? ' AND id = ?' : ''}
	       ORDER BY updated_at DESC`,
	      reviewParams
	    );
    const reviewRows = resultRowsToArray(reviewResult).map(mapRowToReview);

    for (const review of reviewRows) {
      if (!this.hasMeaningfulReviewRecoveryData(review)) {
        continue;
      }

	      items.push({
	        id: `review:${review.id}:notes`,
	        kind: 'review',
	        reviewId: review.id,
	        reviewAddress: review.address || null,
	        reviewName: review.name || null,
	        entityId: review.id,
	        title: 'Review notes',
	        localSummary: this.summarizeReviewRecoveryData(review),
        serverSummary: review.conflictStatus === 'server_wins'
          ? 'Online version is newer; keeping online is recommended.'
          : 'Changes saved on this device are waiting for review.',
        status: review.status,
        syncStatus: review.syncStatus,
        conflictStatus: review.conflictStatus,
	        localUpdatedAt: review.lastLocalMutationAt || review.updatedAt,
	        serverUpdatedAt: review.lastServerSeenAt,
	        recommendation: review.conflictStatus === 'server_wins' ? 'keep_server' : 'recover_local',
	        detailRows: this.buildRecoveryDetailRows({
	          Status: review.status,
	          Notes: review.notes,
	          Projects: review.onDemandProjects,
	          'Vendor notes': review.vendorNotes,
	        }),
	        localData: {
          notes: review.notes,
          onDemandProjects: review.onDemandProjects,
          vendorNotes: review.vendorNotes,
          status: review.status,
        },
      });
    }

	    const responseResult = await this.executeSql(
	      `SELECT responses.* FROM responses
	       LEFT JOIN reviews current_review
	         ON current_review.id = responses.review_id
	        AND current_review.environment_key = responses.environment_key
	       WHERE responses.environment_key = ?
	         AND (
	           (
	             responses.conflict_status IN ('local_wins', 'conflicted', 'restored')
	             AND (
	               responses.sync_status NOT IN ('pending', 'syncing')
	               OR responses.conflict_status = 'conflicted'
	               OR (responses.sync_status = 'pending' AND current_review.id IS NULL)
	             )
	           )
	           OR (responses.sync_status IN ('pending', 'syncing', 'failed') AND current_review.id IS NULL)
	         )${reviewId ? ' AND responses.review_id = ?' : ''}
	       ORDER BY responses.updated_at DESC`,
	      reviewParams
	    );
    const responses = resultRowsToArray(responseResult).map(mapRowToResponse);
    const photos = await this.getPhotosFromQueueForEnvironment(environmentKey).catch(() => []);

    for (const response of responses) {
      if (!this.hasMeaningfulResponseRecoveryData(response)) {
        continue;
      }

	      const queuedPhotos = photos.filter(photo =>
	        photo.reviewResponseId === response.id ||
	        Boolean(response.tempId && photo.tempId === response.tempId)
	      );
      const review = await this.getReviewForEnvironment(response.reviewId, environmentKey).catch(() => null);
		      const itemName = this.getRecoveryItemName(response.itemSnapshot) ||
		        await this.findCachedItemName(response.itemId, response.reviewId) ||
		        (response.itemId ? `Item ${response.itemId}` : `Response ${response.id}`);
	      const itemKey = response.itemId != null ? String(response.itemId) : `response:${response.id}`;
	      const blockedReason = review ? null : 'This review is not available in this environment';

	      items.push({
	        id: `response:${response.reviewId}:${itemKey}`,
	        kind: 'response',
	        reviewId: response.reviewId,
	        reviewAddress: review?.address || null,
	        reviewName: review?.name || null,
	        entityId: response.id,
	        itemId: response.itemId,
	        itemName,
	        floor: response.floor || null,
	        title: itemName,
	        localSummary: this.summarizeResponseRecoveryData(response, queuedPhotos.length),
        serverSummary: response.conflictStatus === 'server_wins'
          ? 'Online response is newer or already complete.'
          : response.conflictStatus === 'local_wins'
          ? 'Changes saved on this device are newer, more complete, or missing online.'
          : 'Changes saved on this device are waiting to sync.',
        status: response.status,
        syncStatus: response.syncStatus,
        conflictStatus: response.conflictStatus,
        localUpdatedAt: response.lastLocalMutationAt || response.updatedAt,
	        serverUpdatedAt: response.lastServerSeenAt,
	        recommendation: blockedReason ? 'needs_review' : response.conflictStatus === 'server_wins' ? 'keep_server' : 'recover_local',
	        photoCount: queuedPhotos.length,
	        photoPreviews: queuedPhotos.map(photo => ({
	          id: photo.id ?? photo.localPath,
	          localPath: photo.localPath,
	          fileName: photo.fileName,
	          caption: photo.caption,
	          status: photo.status,
	        })),
	        blockedReason,
	        detailRows: this.buildRecoveryDetailRows({
	          Status: response.status,
	          Presence: response.isPresent ? 'Marked present' : 'Marked not present',
	          Notes: response.notes,
	          Answers: this.hasMeaningfulResponseData(response.responseData) ? 'Answers saved' : null,
	          Room: response.roomId ? `Room ${response.roomId}` : null,
	          Floor: response.floor,
	          Photos: queuedPhotos.length > 0 ? `${queuedPhotos.length} photo${queuedPhotos.length !== 1 ? 's' : ''}` : null,
	        }),
	        localData: {
          isPresent: response.isPresent,
          status: response.status,
          notes: response.notes,
          responseData: response.responseData,
          roomId: response.roomId,
          floor: response.floor,
          photoCount: queuedPhotos.length,
        },
      });
    }

    return items.filter(item => Number.isFinite(item.reviewId));
  }

  private async getReviewForEnvironment(
    reviewId: number,
    environmentKey: string
  ): Promise<OfflineReview | null> {
    const result = await this.executeSql(
      'SELECT * FROM reviews WHERE id = ? AND environment_key = ? LIMIT 1',
      [reviewId, environmentKey]
    );

    if (result.rows.length === 0) {
      return null;
    }

    return mapRowToReview(result.rows.item(0));
  }

  private async getPhotosFromQueueForEnvironment(environmentKey: string): Promise<PhotoQueueItem[]> {
    const statement = buildPhotoQueueSelectStatement({ environmentKey });
    const result = await this.executeSql(statement.sql, statement.params);
    return mapResultRows(result, mapRowToPhotoQueueItem);
  }

	  private buildRecoveryDetailRows(values: Record<string, unknown>): Array<{ label: string; value: string }> {
	    return Object.entries(values)
	      .filter(([, value]) => {
	        if (value == null) return false;
	        if (typeof value === 'string') return value.trim().length > 0;
	        return true;
	      })
	      .map(([label, value]) => ({
	        label,
	        value: String(value),
	      }));
	  }

		  private async findCachedItemName(itemId?: number | null, reviewId?: number | null): Promise<string | null> {
		    if (!itemId) {
		      return null;
		    }

		    if (reviewId) {
		      const snapshotName = await this.findItemNameInReviewTemplateSnapshot(reviewId, itemId);
		      if (snapshotName) {
		        return snapshotName;
		      }
		    }

		    try {
		      const result = await this.executeSql(
		        'SELECT data FROM items_cache WHERE id = ? LIMIT 1',
	        [itemId]
	      );
	      const row = getFirstResultRow<{ data?: string }>(result);
	      if (!row?.data) {
	        return null;
	      }

	      const item = JSON.parse(row.data);
	      return typeof item?.name === 'string' && item.name.trim().length > 0
	        ? item.name
	        : null;
	    } catch {
	      return null;
		    }
		  }

		  private async findItemNameInReviewTemplateSnapshot(reviewId: number, itemId: number): Promise<string | null> {
		    const template = await this.getReviewTemplateSnapshot(reviewId);
		    const categories = this.getTemplateCategories(template);
		    return this.findItemNameInCategories(categories, itemId);
		  }

		  private getTemplateCategories(template: ReviewTemplate | any | null): any[] | undefined {
		    if (!template) {
		      return undefined;
		    }

		    if (Array.isArray(template.categories)) return template.categories;
		    if (Array.isArray(template.reviewCategories)) return template.reviewCategories;
		    if (Array.isArray(template.review_categories)) return template.review_categories;
		    if (Array.isArray(template.templateCategories)) return template.templateCategories;
		    if (Array.isArray(template.template_categories)) return template.template_categories;
		    return undefined;
		  }

		  private getCategoryItems(category: ReviewCategory | any): any[] {
		    const itemCollections = [
		      category.items,
		      category.reviewItems,
		      category.review_items,
		      category.templateItems,
		      category.template_items,
		      category.reviewTemplateItems,
		      category.review_template_items,
		    ];

		    return itemCollections.find(Array.isArray) || [];
		  }

		  private getSubcategories(category: ReviewCategory | any): any[] {
		    const subcategoryCollections = [
		      category.subcategories,
		      category.children,
		      category.categories,
		      category.reviewCategories,
		      category.review_categories,
		      category.templateCategories,
		      category.template_categories,
		    ];

		    return subcategoryCollections.find(Array.isArray) || [];
		  }

		  private getRecoveryItemName(item: unknown): string | null {
		    if (!item || typeof item !== 'object') {
		      return null;
		    }

		    const candidate = item as Record<string, unknown>;
		    const name = candidate.name || candidate.title || candidate.label;
		    return typeof name === 'string' && name.trim().length > 0
		      ? name.trim()
		      : null;
		  }

		  private findItemNameInCategories(categories: any[] | undefined, itemId: number): string | null {
		    if (!categories?.length) {
		      return null;
		    }

		    for (const category of categories) {
		      const items = this.getCategoryItems(category);

		      const item = items.find((candidate: ReviewItem | any) =>
		        Number(candidate?.id ?? candidate?.itemId) === Number(itemId)
		      );
		      const itemName = this.getRecoveryItemName(item);
		      if (itemName) {
		        return itemName;
		      }

		      const nestedName = this.findItemNameInCategories(
		        this.getSubcategories(category),
		        itemId
		      );
		      if (nestedName) {
		        return nestedName;
		      }
		    }

		    return null;
		  }

	  private hasMeaningfulReviewRecoveryData(review: OfflineReview): boolean {
    return Boolean(
      this.hasMeaningfulText(review.notes) ||
      this.hasMeaningfulText(review.onDemandProjects) ||
      this.hasMeaningfulText(review.vendorNotes) ||
      review.status === 'completed' ||
      review.status === 'submitted'
    );
  }

  private hasMeaningfulResponseRecoveryData(response: OfflineResponse): boolean {
    const statusPriority = this.responseStatusPriority(response.status);
    return Boolean(
      statusPriority > 0 ||
      this.hasMeaningfulText(response.notes) ||
      this.hasMeaningfulResponseData(response.responseData)
    );
  }

  private hasMeaningfulText(value: unknown): boolean {
    return typeof value === 'string' && value.trim().length > 0;
  }

  private hasMeaningfulResponseData(value: unknown): boolean {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return false;
    }

    return Object.values(value as Record<string, unknown>).some(entry => {
      if (entry == null) return false;
      if (typeof entry === 'string') return entry.trim().length > 0;
      if (Array.isArray(entry)) return entry.length > 0;
      if (typeof entry === 'object') return Object.keys(entry as Record<string, unknown>).length > 0;
      return true;
    });
  }

  private responseStatusPriority(status: unknown): number {
    if (status === 'completed') return 2;
    if (status === 'in_progress') return 1;
    return 0;
  }

  private summarizeReviewRecoveryData(review: OfflineReview): string {
    const parts: string[] = [];

    if (review.status === 'completed' || review.status === 'submitted') {
      parts.push(`Status: ${review.status}`);
    }
    if (this.hasMeaningfulText(review.notes)) {
      parts.push(`Notes: ${this.truncateRecoveryText(review.notes)}`);
    }
    if (this.hasMeaningfulText(review.onDemandProjects)) {
      parts.push(`Projects: ${this.truncateRecoveryText(review.onDemandProjects)}`);
    }
    if (this.hasMeaningfulText(review.vendorNotes)) {
      parts.push(`Vendor notes: ${this.truncateRecoveryText(review.vendorNotes)}`);
    }

    return parts.join(' | ') || 'Saved review data';
  }

  private summarizeResponseRecoveryData(response: OfflineResponse, photoCount: number): string {
    const parts: string[] = [];

    if (response.status) {
      parts.push(`Status: ${response.status}`);
    }
    parts.push(response.isPresent ? 'Marked present' : 'Marked not present');
    if (this.hasMeaningfulText(response.notes)) {
      parts.push(`Notes: ${this.truncateRecoveryText(response.notes)}`);
    }
    if (this.hasMeaningfulResponseData(response.responseData)) {
      parts.push('Answers saved');
    }
    if (photoCount > 0) {
      parts.push(`${photoCount} photo${photoCount !== 1 ? 's' : ''}`);
    }

    return parts.join(' | ');
  }

  private truncateRecoveryText(value: unknown): string {
    if (typeof value !== 'string') {
      return '';
    }

    const normalized = value.replace(/\s+/g, ' ').trim();
    return normalized.length > 96 ? `${normalized.slice(0, 93)}...` : normalized;
  }

  async getReviewIdsWithRecoverableWork(): Promise<number[]> {
    await this.initialize();

	    const result = await this.executeSql(
	      `SELECT DISTINCT review_id as review_id FROM offline_mutation_journal
	       WHERE review_id IS NOT NULL AND environment_key = ? AND status = 'conflict'
	       UNION
	       SELECT DISTINCT responses.review_id FROM responses
	       LEFT JOIN reviews current_review
	         ON current_review.id = responses.review_id
	        AND current_review.environment_key = responses.environment_key
	       WHERE responses.environment_key = ?
	         AND (
	           (
	             responses.conflict_status IN ('local_wins', 'conflicted', 'restored')
	             AND (
	               responses.sync_status NOT IN ('pending', 'syncing')
	               OR responses.conflict_status = 'conflicted'
	               OR (responses.sync_status = 'pending' AND current_review.id IS NULL)
	             )
	           )
	           OR (responses.sync_status IN ('pending', 'syncing', 'failed') AND current_review.id IS NULL)
	         )
	       UNION
	       SELECT DISTINCT id as review_id FROM reviews
	       WHERE environment_key = ?
	         AND sync_status NOT IN ('pending', 'syncing')
	         AND conflict_status IN ('local_wins', 'conflicted', 'restored')`,
	      [this.getEnvironmentKey(), this.getEnvironmentKey(), this.getEnvironmentKey()]
	    );

    return resultRowsToArray(result)
      .map(row => Number(row.review_id))
      .filter(reviewId => Number.isFinite(reviewId));
  }

  // ====================
  // CACHE OPERATIONS
  // ====================

  /**
   * Cache review templates with TTL
   */
  async cacheTemplates(templates: ReviewTemplate[], ttlHours: number = 24): Promise<void> {
    await this.initialize();

    const expiresAt = new Date();
    expiresAt.setHours(expiresAt.getHours() + ttlHours);

    try {
      if (!this.db) {
        throw new Error('Database not initialized');
      }

      // Only update cache if we have valid data
      if (!templates || templates.length === 0) {
        console.error('[OfflineDB] Refusing to cache empty templates array - keeping existing cache');
        return;
      }

      // Validate that templates have proper structure
      const validTemplates = templates.filter(t => {
        const isValid = t && t.id;
        if (!isValid) {
          console.error(`[OfflineDB] Template is invalid - missing ID`);
        }
        return isValid;
      });

      if (validTemplates.length === 0) {
        console.error('[OfflineDB] No valid templates to cache - keeping existing cache');
        return;
      }

      // Smart merge: don't overwrite full templates with empty ones
      const existingTemplates = await this.getCachedTemplates().catch((): ReviewTemplate[] => []);
      const existingMap = new Map<number, ReviewTemplate>(existingTemplates.map(t => [t.id, t]));

      const templatesToCache = validTemplates.filter(newTemplate => {
        const existing = existingMap.get(newTemplate.id);

        // If no existing template, cache the new one
        if (!existing) {
          return true;
        }

        // Check if new template is "worse" than existing (has fewer categories)
        const newHasCategories = newTemplate.categories && newTemplate.categories.length > 0;
        const existingHasCategories = existing.categories && existing.categories.length > 0;

        if (existingHasCategories && !newHasCategories) {
          console.error(`[OfflineDB] Skipping template ${newTemplate.id} - would overwrite full template (${existing.categories.length} categories) with empty one`);
          return false;
        }

        // New template is same or better, cache it
        return true;
      });

      if (templatesToCache.length === 0) {
        console.error('[OfflineDB] All templates would downgrade existing cache - keeping current versions');
        return;
      }

      await this.db.transaction((tx: Transaction) => {
        // Delete only the templates we're actually updating (not all templates)
        const deleteStatement = buildTemplatesCacheDeleteByIdsStatement(
          templatesToCache.map(t => t.id)
        );
        tx.executeSql(deleteStatement.sql, deleteStatement.params);

        // Insert new templates (only those that passed the smart merge filter)
        templatesToCache.forEach((template) => {
          const insertStatement = buildTemplateCacheInsertStatement(
            template.id,
            stringifyCacheData(template),
            expiresAt.toISOString()
          );
          tx.executeSql(insertStatement.sql, insertStatement.params);
        });
      });

      await this.updateCacheMetadata('templates', new Date());
    } catch (error) {
      console.error('[OfflineDB] Error caching templates:', error);
      throw new Error(`Failed to cache templates: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get cached templates
   * If no valid (non-expired) templates exist, returns expired templates as fallback
   * for offline mode resilience
   */
  async getCachedTemplates(): Promise<ReviewTemplate[]> {
    await this.initialize();

    try {
      // First try to get valid (non-expired) templates
      const selectStatement = buildValidTemplatesCacheSelectStatement(new Date().toISOString());
      const result = await this.executeSql(selectStatement.sql, selectStatement.params);

      const templates = mapCachedDataRows<ReviewTemplate>(result);

      // If we have valid templates, return them
      if (templates.length > 0) {
        return templates;
      }

      // Fallback: Return expired templates if no valid ones exist
      // This ensures offline mode still works with stale data rather than nothing
      const fallbackStatement = buildTemplatesCacheFallbackSelectStatement();
      const fallbackResult = await this.executeSql(fallbackStatement.sql, fallbackStatement.params);

      const expiredTemplates = mapCachedDataRows<ReviewTemplate>(fallbackResult);

      if (expiredTemplates.length > 0) {
        console.warn(`[OfflineDB] No valid templates found, returning ${expiredTemplates.length} expired templates as fallback`);
      }

      return expiredTemplates;
    } catch (error) {
      console.error('[OfflineDB] Error getting cached templates:', error);
      throw new Error(`Failed to get cached templates: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Persist a historical template snapshot for a specific review
   */
  async saveReviewTemplateSnapshot(reviewId: number, template: ReviewTemplate): Promise<void> {
    await this.initialize();

    if (!template || !reviewId) {
      return;
    }

    const now = new Date().toISOString();
    const payload = stringifyCacheData(template);

    try {
      const upsertStatement = buildReviewTemplateSnapshotUpsertStatement(reviewId, payload, now);
      await this.executeSql(upsertStatement.sql, upsertStatement.params);
    } catch (error) {
      console.error('[OfflineDB] Failed to save template snapshot:', error);
      throw error;
    }
  }

  /**
   * Retrieve a stored historical template snapshot for a review
   */
  async getReviewTemplateSnapshot(reviewId: number): Promise<ReviewTemplate | null> {
    await this.initialize();

    try {
      const selectStatement = buildReviewTemplateSnapshotSelectStatement(reviewId);
      const result = await this.executeSql(selectStatement.sql, selectStatement.params);

      if (result.rows.length === 0) {
        return null;
      }

      return getCachedDataRow<ReviewTemplate>(result, 'template_data');
    } catch (error) {
      console.error('[OfflineDB] Failed to load template snapshot:', error);
      return null;
    }
  }

  /**
   * Remove a stored template snapshot (e.g., when review is deleted)
   */
  async deleteReviewTemplateSnapshot(reviewId: number): Promise<void> {
    await this.initialize();

    try {
      const deleteStatement = buildReviewTemplateSnapshotDeleteStatement(reviewId);
      await this.executeSql(deleteStatement.sql, deleteStatement.params);
    } catch (error) {
      console.error('[OfflineDB] Failed to delete template snapshot:', error);
      throw error;
    }
  }

  /**
   * Cache review categories with TTL
   */
  async cacheCategories(categories: ReviewCategory[], ttlHours: number = 24): Promise<void> {
    await this.initialize();

    const expiresAt = new Date();
    expiresAt.setHours(expiresAt.getHours() + ttlHours);

    try {
      if (!this.db) {
        throw new Error('Database not initialized');
      }

      await this.db.transaction((tx: Transaction) => {
        // Clear existing categories
        const clearStatement = buildCategoriesCacheClearStatement();
        tx.executeSql(clearStatement.sql, clearStatement.params);

        // Insert new categories
        categories.forEach((category) => {
          const insertStatement = buildCategoryCacheInsertStatement(
            category.id,
            category.templateId,
            stringifyCacheData(category),
            expiresAt.toISOString()
          );
          tx.executeSql(insertStatement.sql, insertStatement.params);
        });
      });

    } catch (error) {
      console.warn('[OfflineDB] Error caching categories:', error);
      throw new Error(`Failed to cache categories: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get cached categories by template ID
   */
  async getCachedCategories(templateId?: number): Promise<ReviewCategory[]> {
    await this.initialize();

    try {
      const statement = buildCategoriesCacheSelectStatement(new Date().toISOString(), templateId);
      const result = await this.executeSql(statement.sql, statement.params);
      return mapCachedDataRows<ReviewCategory>(result);
    } catch (error) {
      console.warn('[OfflineDB] Error getting cached categories:', error);
      throw new Error(`Failed to get cached categories: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Cache review items with TTL
   */
  async cacheItems(items: ReviewItem[], ttlHours: number = 24): Promise<void> {
    await this.initialize();

    const expiresAt = new Date();
    expiresAt.setHours(expiresAt.getHours() + ttlHours);

    try {
      if (!this.db) {
        throw new Error('Database not initialized');
      }

      await this.db.transaction((tx: Transaction) => {
        // Clear existing items
        const clearStatement = buildItemsCacheClearStatement();
        tx.executeSql(clearStatement.sql, clearStatement.params);

        // Insert new items
        items.forEach((item) => {
          const insertStatement = buildItemCacheInsertStatement(
            item.id,
            item.categoryId,
            stringifyCacheData(item),
            expiresAt.toISOString()
          );
          tx.executeSql(insertStatement.sql, insertStatement.params);
        });
      });

    } catch (error) {
      console.warn('[OfflineDB] Error caching items:', error);
      throw new Error(`Failed to cache items: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get cached items by category ID
   */
  async getCachedItems(categoryId?: number): Promise<ReviewItem[]> {
    await this.initialize();

    try {
      const statement = buildItemsCacheSelectStatement(new Date().toISOString(), categoryId);
      const result = await this.executeSql(statement.sql, statement.params);
      return mapCachedDataRows<ReviewItem>(result);
    } catch (error) {
      console.warn('[OfflineDB] Error getting cached items:', error);
      throw new Error(`Failed to get cached items: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Cache rooms with TTL
   */
  async cacheRooms(rooms: Room[], propertyId?: number, ttlHours: number = 24): Promise<void> {
    await this.initialize();

    const expiresAt = new Date();
    expiresAt.setHours(expiresAt.getHours() + ttlHours);

    try {
      if (!this.db) {
        throw new Error('Database not initialized');
      }

      await this.db.transaction((tx: Transaction) => {
        // Clear existing rooms for the property if specified
        if (propertyId !== undefined) {
          const clearStatement = buildRoomsCacheClearForPropertyStatement(propertyId);
          tx.executeSql(clearStatement.sql, clearStatement.params);
        } else {
          // Only delete defaults, preserve property-specific rooms
          const clearStatement = buildRoomsCacheClearDefaultStatement();
          tx.executeSql(clearStatement.sql, clearStatement.params);
        }

        // Insert new rooms
        rooms.forEach((room) => {
          // Normalize property_id: use argument if provided, otherwise extract from room
          // Treat 'default', 'defaults', null, undefined, or non-numeric as NULL
          let cachePropertyId: number | null = null;

          if (propertyId !== undefined) {
            // Use explicit argument (from cacheRooms call)
            // Handle both numbers and numeric strings (SQLite can return either)
            if (typeof propertyId === 'number') {
              cachePropertyId = propertyId;
            } else {
              const parsed = parseInt(String(propertyId));
              cachePropertyId = !isNaN(parsed) ? parsed : null;
            }
          } else if (room.propertyId) {
            // Extract from room object, but only if numeric
            const parsed = typeof room.propertyId === 'number'
              ? room.propertyId
              : parseInt(String(room.propertyId));
            cachePropertyId = !isNaN(parsed) ? parsed : null;
          }

          const insertStatement = buildRoomCacheInsertStatement(
            room.id,
            cachePropertyId,
            stringifyCacheData(room),
            expiresAt.toISOString()
          );
          tx.executeSql(insertStatement.sql, insertStatement.params);
        });
      });

    } catch (error) {
      console.error('[OfflineDB] ❌ Error caching rooms:', {
        error: error instanceof Error ? error.message : 'Unknown',
        code: (error as any).code,
        propertyId: propertyId,
        roomCount: rooms.length,
        tempIds: rooms.filter(r => (r as any).tempId).map(r => (r as any).tempId).slice(0, 3)
      });
      throw new Error(`Failed to cache rooms: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get cached rooms by property ID
   */
  async getCachedRooms(propertyId?: number): Promise<Room[]> {
    await this.initialize();

    try {
      const statement = buildRoomsCacheSelectStatement(new Date().toISOString(), propertyId);
      const result = await this.executeSql(statement.sql, statement.params);
      return mapCachedDataRows<Room>(result);
    } catch (error) {
      console.warn('[OfflineDB] Error getting cached rooms:', error);
      throw new Error(`Failed to get cached rooms: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  // ====================
  // UTILITY OPERATIONS
  // ====================

  /**
   * Clear expired cache entries
   * NOTE: Templates are NOT deleted here to preserve offline fallback.
   * Expired templates will be replaced when cacheTemplates() is called with fresh data.
   * This prevents empty cache if network fails during refresh.
   */
  async clearExpiredCache(): Promise<void> {
    await this.initialize();

    const now = new Date().toISOString();

    try {
      if (!this.db) {
        throw new Error('Database not initialized');
      }

      await this.db.transaction((tx: Transaction) => {
        // IMPORTANT: Don't delete templates_cache - keep expired templates as offline fallback
        // Templates will be replaced when cacheTemplates() is called with fresh data
        buildExpiredCacheDeleteStatements(now).forEach((statement) => {
          tx.executeSql(statement.sql, statement.params);
        });
      });

    } catch (error) {
      console.warn('[OfflineDB] Error clearing expired cache:', error);
      throw new Error(`Failed to clear expired cache: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Clear expired templates that are older than a threshold (e.g., 30 days)
   * This is called separately to prevent unbounded SQLite growth while still
   * preserving recently expired templates as fallback
   */
  async clearStaleTemplates(maxAgeHours: number = 720): Promise<void> {
    await this.initialize();

    // Calculate threshold: templates expired more than maxAgeHours ago
    const threshold = new Date();
    threshold.setHours(threshold.getHours() - maxAgeHours);

    try {
      if (!this.db) {
        throw new Error('Database not initialized');
      }

      await this.db.transaction((tx: Transaction) => {
        const statement = buildStaleTemplatesCacheDeleteStatement(threshold.toISOString());
        tx.executeSql(statement.sql, statement.params);
      });
    } catch (error) {
      console.warn('[OfflineDB] Error clearing stale templates:', error);
      // Don't throw - this is cleanup, not critical
    }
  }

  /**
   * Get database statistics
   */
  async getDatabaseStats(): Promise<{
    reviews: number;
    responses: number;
    photosQueue: number;
    cachedTemplates: number;
    cachedCategories: number;
    cachedItems: number;
    cachedRooms: number;
  }> {
    await this.initialize();

    try {
      const now = new Date().toISOString();
	      const statements = buildDatabaseStatsStatements(now, this.getEnvironmentKey());
      const results = await Promise.all([
        this.executeSql(statements.reviews.sql, statements.reviews.params),
        this.executeSql(statements.responses.sql, statements.responses.params),
        this.executeSql(statements.photosQueue.sql, statements.photosQueue.params),
        this.executeSql(statements.cachedTemplates.sql, statements.cachedTemplates.params),
        this.executeSql(statements.cachedCategories.sql, statements.cachedCategories.params),
        this.executeSql(statements.cachedItems.sql, statements.cachedItems.params),
        this.executeSql(statements.cachedRooms.sql, statements.cachedRooms.params),
      ]);

      return {
        reviews: results[0].rows.item(0).count,
        responses: results[1].rows.item(0).count,
        photosQueue: results[2].rows.item(0).count,
        cachedTemplates: results[3].rows.item(0).count,
        cachedCategories: results[4].rows.item(0).count,
        cachedItems: results[5].rows.item(0).count,
        cachedRooms: results[6].rows.item(0).count,
      };
    } catch (error) {
      console.warn('[OfflineDB] Error getting database stats:', error);
      throw new Error(`Failed to get database stats: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Close database connection
   */
  async close(): Promise<void> {
    if (this.db) {
      try {
        await this.db.close();
        this.db = null;
        this.isInitialized = false;
      } catch (error) {
        console.warn('[OfflineDB] Error closing database:', error);
        throw new Error(`Failed to close database: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }
  }

  /**
   * Execute a transaction with proper error handling
   */
  async executeTransaction<T>(
    callback: (tx: Transaction) => Promise<T>
  ): Promise<T> {
    await this.initialize();

    if (!this.db) {
      throw new Error('Database not initialized');
    }

    return new Promise((resolve, reject) => {
      this.db!.transaction(
        async (tx: Transaction) => {
          try {
            const result = await callback(tx);
            resolve(result);
          } catch (error) {
            reject(error);
          }
        },
        (error: SQLError) => {
          console.warn('[OfflineDB] Transaction error:', error);
          reject(new Error(`Transaction failed: ${error.message}`));
        }
      );
    });
  }

  /**
   * Create offline custom room
   */
  async createOfflineRoom(propertyId: number, name: string, displayOrder: number = 0): Promise<string> {
    if (!this.db) {
      throw new Error('Database not initialized');
    }

	    const tempId = `room_${Date.now()}_${Math.random()}`;
	    const insertStatement = buildOfflineRoomInsertStatement({
	      environmentKey: this.getEnvironmentKey(),
	      tempId,
      propertyId,
      name,
      displayOrder,
    });

    return new Promise((resolve, reject) => {
      this.db!.transaction((tx: Transaction) => {
        tx.executeSql(
          insertStatement.sql,
          insertStatement.params,
          (_: Transaction, result: ResultSet) => {
            resolve(tempId);
          },
          (_: Transaction, error: SQLError) => {
            console.warn('[OfflineDB] Error creating offline room:', error);
            reject(new Error(`Failed to create offline room: ${error.message}`));
            return false;
          }
        );
      });
    });
  }

  /**
   * Get offline rooms by property ID
   */
  async getOfflineRooms(propertyId?: number): Promise<any[]> {
    if (!this.db) {
      throw new Error('Database not initialized');
    }

	    const selectStatement = buildOfflineRoomsSelectStatement(propertyId, this.getEnvironmentKey());

    return new Promise((resolve, reject) => {
      this.db!.transaction((tx: Transaction) => {
        tx.executeSql(
          selectStatement.sql,
          selectStatement.params,
          (_: Transaction, result: ResultSet) => {
            resolve(resultRowsToArray(result));
          },
          (_: Transaction, error: SQLError) => {
            console.warn('[OfflineDB] Error getting offline rooms:', error);
            reject(new Error(`Failed to get offline rooms: ${error.message}`));
            return false;
          }
        );
      });
    });
  }

  /**
   * Update offline room sync status
   */
  async updateOfflineRoomSyncStatus(
    tempId: string,
    syncStatus: OfflineEntitySyncStatus,
    syncError?: string,
    realId?: number
  ): Promise<void> {
    if (!this.db) {
      throw new Error('Database not initialized');
    }

    return new Promise((resolve, reject) => {
      this.db!.transaction((tx: Transaction) => {
        const updateStatement = buildOfflineRoomSyncStatusUpdateStatement(
          tempId,
	          syncStatus,
	          syncError,
	          realId,
	          this.getEnvironmentKey()
        );

        tx.executeSql(
          updateStatement.sql,
          updateStatement.params,
          () => {
            resolve();
          },
          (_: Transaction, error: SQLError) => {
            console.warn('[OfflineDB] Error updating offline room sync status:', error);
            reject(new Error(`Failed to update offline room sync status: ${error.message}`));
            return false;
          }
        );
      });
    });
  }

  /**
   * Save temp ID to real ID mapping
   */
  async saveIdMapping(tempId: string, realId: number, entityType: IdMappingEntityType): Promise<void> {
    if (!this.db) {
      throw new Error('Database not initialized');
    }

	    const insertStatement = buildIdMappingInsertStatement(tempId, realId, entityType, this.getEnvironmentKey());

    return new Promise((resolve, reject) => {
      this.db!.transaction((tx: Transaction) => {
        tx.executeSql(
          insertStatement.sql,
          insertStatement.params,
          (_: Transaction, result: ResultSet) => {
            resolve();
          },
          (_: Transaction, error: SQLError) => {
            console.warn('[OfflineDB] Error saving ID mapping:', error);
            reject(new Error(`Failed to save ID mapping: ${error.message}`));
            return false;
          }
        );
      });
    });
  }

  /**
   * Get real ID from temp ID
   */
  async getRealIdFromTempId(tempId: string): Promise<number | null> {
    if (!this.db) {
      throw new Error('Database not initialized');
    }

	    const selectStatement = buildRealIdByTempIdSelectStatement(tempId, this.getEnvironmentKey());

    return new Promise((resolve, reject) => {
      this.db!.transaction((tx: Transaction) => {
        tx.executeSql(
          selectStatement.sql,
          selectStatement.params,
          (_: Transaction, result: ResultSet) => {
            resolve(getFirstResultRow<{ real_id: number }>(result)?.real_id ?? null);
          },
          (_: Transaction, error: SQLError) => {
            console.warn('[OfflineDB] Error getting real ID:', error);
            reject(new Error(`Failed to get real ID: ${error.message}`));
            return false;
          }
        );
      });
    });
  }

  /**
   * Get temp ID by real ID (reverse lookup for synced items)
   */
  async getTempIdByRealId(realId: number, entityType: IdMappingEntityType): Promise<string | null> {
    if (!this.db) {
      throw new Error('Database not initialized');
    }

	    const selectStatement = buildTempIdByRealIdSelectStatement(realId, entityType, this.getEnvironmentKey());

    return new Promise((resolve, reject) => {
      this.db!.transaction((tx: Transaction) => {
        tx.executeSql(
          selectStatement.sql,
          selectStatement.params,
          (_: Transaction, result: ResultSet) => {
            resolve(getFirstResultRow<{ temp_id: string }>(result)?.temp_id ?? null);
          },
          (_: Transaction, error: SQLError) => {
            console.error('[OfflineDB] ❌ Error getting temp ID by real ID:', error);
            reject(new Error(`Failed to get temp ID: ${error.message}`));
            return false;
          }
        );
      });
    });
  }

  /**
   * Get all ID mappings
   */
  async getAllIdMappings(): Promise<Array<{ tempId: string; realId: number; entityType: string; syncedAt: string }>> {
    if (!this.db) {
      throw new Error('Database not initialized');
    }

	    const selectStatement = buildAllIdMappingsSelectStatement(this.getEnvironmentKey());

    return new Promise((resolve, reject) => {
      this.db!.transaction((tx: Transaction) => {
        tx.executeSql(
          selectStatement.sql,
          selectStatement.params,
          (_: Transaction, result: ResultSet) => {
            resolve(mapResultRows(result, mapRowToIdMapping));
          },
          (_: Transaction, error: SQLError) => {
            console.warn('[OfflineDB] Error getting ID mappings:', error);
            reject(new Error(`Failed to get ID mappings: ${error.message}`));
            return false;
          }
        );
      });
    });
  }

  /**
   * Update all responses with old room ID to new room ID
   */
  async updateResponseRoomIdMapping(oldRoomId: number, newRoomId: number): Promise<void> {
    if (!this.db) {
      throw new Error('Database not initialized');
    }

	    const updateStatement = buildResponseRoomIdMappingUpdateStatement(oldRoomId, newRoomId, this.getEnvironmentKey());

    return new Promise((resolve, reject) => {
      this.db!.transaction((tx: Transaction) => {
        tx.executeSql(
          updateStatement.sql,
          updateStatement.params,
          (_: Transaction, result: ResultSet) => {
            resolve();
          },
          (_: Transaction, error: SQLError) => {
            console.warn('[OfflineDB] Error updating room ID mapping:', error);
            reject(new Error(`Failed to update room ID mapping: ${error.message}`));
            return false;
          }
        );
      });
    });
  }

  /**
   * Update cached room with new ID
   */
  async updateCachedRoom(propertyId: number, oldId: number, newId: number): Promise<void> {
    if (!this.db) {
      throw new Error('Database not initialized');
    }

    try {
      // Get current cached rooms
      const rooms = await this.getCachedRooms(propertyId).catch(() => []);

      // Find and update the room, or add new entry if not found
      const roomIndex = rooms.findIndex(room => room.id === oldId);

      if (roomIndex !== -1) {
        // Update existing room
        rooms[roomIndex] = { ...rooms[roomIndex], id: newId, tempId: undefined };
      } else {
        // Room not in cache - fetch from offline_rooms and add
        const pendingRooms = await this.getPendingOfflineRooms();
        const offlineRoom = pendingRooms.find(r => {
          const tempNegativeId = -Math.abs(parseInt(r.temp_id.replace(/\D/g, '')) || 0);
          return tempNegativeId === oldId;
        });

        if (offlineRoom) {
          rooms.push({
            id: newId,
            propertyId: offlineRoom.property_id,
            name: offlineRoom.name,
            displayOrder: offlineRoom.display_order || 0,
            isCustom: true,
            tempId: undefined, // Clear tempId for synced room
          });
        } else {
          console.error('[OfflineDB] ⚠️ Room', oldId, 'not found in cache or pending rooms');
        }
      }

      // Save updated rooms back to cache
      await this.cacheRooms(rooms, propertyId, 24);
    } catch (error) {
      console.warn('[OfflineDB] Error updating cached room:', error);
      throw new Error(`Failed to update cached room: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Create offline on-demand item
   */
  async createOfflineOnDemandItem(
    itemData: {
      id?: number;
      reviewId: number;
      name: string;
      description?: string;
      categoryId?: number;
      questionsSchema?: any;
      createdAt?: string;
      syncStatus?: string;
    } | number,
    name?: string,
    description?: string,
    categoryId?: number,
    questionsSchema?: any
  ): Promise<{ id: number; tempId: string }> {
    if (!this.db) {
      throw new Error('Database not initialized');
    }

    // Handle both object and individual parameter calls
    let reviewId: number, itemName: string, itemDesc: string | undefined,
        catId: number | undefined, qSchema: any, tempId: string, syncStatus: string;

    if (typeof itemData === 'object') {
      // Called with object from login-time cache seeding.
      reviewId = itemData.reviewId;
      itemName = itemData.name;
      itemDesc = itemData.description;
      catId = itemData.categoryId;
      qSchema = itemData.questionsSchema;
      syncStatus = itemData.syncStatus || 'pending';
      tempId = itemData.id ? `synced_${itemData.id}` : `item_${Date.now()}_${Math.random()}`;
    } else {
      // Called with individual parameters (legacy)
      reviewId = itemData;
      itemName = name!;
      itemDesc = description;
      catId = categoryId;
      qSchema = questionsSchema;
      syncStatus = 'pending';
      tempId = `item_${Date.now()}_${Math.random()}`;
    }

    return new Promise((resolve, reject) => {
      this.db!.transaction((tx: Transaction) => {
	        const insertStatement = buildOfflineOnDemandItemInsertStatement({
	          environmentKey: this.getEnvironmentKey(),
	          tempId,
          reviewId,
          name: itemName,
          description: itemDesc,
          categoryId: catId,
          questionsSchema: qSchema,
          syncStatus,
        });

        tx.executeSql(
          insertStatement.sql,
          insertStatement.params,
          (_: Transaction, result: ResultSet) => {
            const insertedId = result.insertId;
            resolve({ id: insertedId, tempId });
          },
          (_: Transaction, error: SQLError) => {
            console.warn('[OfflineDB] Error creating offline on-demand item:', error);
            reject(new Error(`Failed to create offline on-demand item: ${error.message}`));
            return false;
          }
        );
      });
    });
  }

  /**
   * Get single offline on-demand item by ID
   */
  async getOfflineOnDemandItem(itemId: number): Promise<any | null> {
    if (!this.db) {
      throw new Error('Database not initialized');
    }

    const lookupId = await this.resolveOfflineOnDemandItemLookupId(itemId);

    return new Promise((resolve, reject) => {
      this.db!.transaction((tx: Transaction) => {
	        const selectStatement = buildOfflineOnDemandItemSelectStatement(lookupId, this.getEnvironmentKey());

        tx.executeSql(
          selectStatement.sql,
          selectStatement.params,
          (_: Transaction, result: ResultSet) => {
            if (result.rows.length > 0) {
              resolve(mapRowToOfflineOnDemandItem(result.rows.item(0), {
                onQuestionsSchemaParseError: (error) => {
                  console.warn('[OfflineDB] Error parsing questions_schema:', error);
                },
              }));
            } else {
              resolve(null);
            }
          },
          (_: Transaction, error: SQLError) => {
            console.warn('[OfflineDB] Error getting offline on-demand item:', error);
            reject(new Error(`Failed to get offline on-demand item: ${error.message}`));
            return false;
          }
        );
      });
    });
  }

  /**
   * Get offline on-demand items by review ID
   */
  async getOfflineOnDemandItems(reviewId: number): Promise<any[]> {
    if (!this.db) {
      throw new Error('Database not initialized');
    }

    return new Promise((resolve, reject) => {
      this.db!.transaction((tx: Transaction) => {
	        const selectStatement = buildOfflineOnDemandItemsByReviewSelectStatement(reviewId, this.getEnvironmentKey());

        tx.executeSql(
          selectStatement.sql,
          selectStatement.params,
          (_: Transaction, result: ResultSet) => {
            resolve(mapResultRows(result, (row) => mapRowToOfflineOnDemandItem(row, {
              normalizeSyncedId: true,
              onQuestionsSchemaParseError: (error) => {
                console.warn('[OfflineDB] Error parsing questions_schema:', error);
              },
            })));
          },
          (_: Transaction, error: SQLError) => {
            console.warn('[OfflineDB] Error getting offline on-demand items:', error);
            reject(new Error(`Failed to get offline on-demand items: ${error.message}`));
            return false;
          }
        );
      });
    });
  }

  /**
   * Update offline on-demand item sync status
   */
  async updateOfflineItemSyncStatus(
    tempId: string,
    syncStatus: OfflineItemSyncStatus,
    syncError?: string,
    realId?: number
  ): Promise<void> {
    if (!this.db) {
      throw new Error('Database not initialized');
    }

    return new Promise((resolve, reject) => {
      this.db!.transaction((tx: Transaction) => {
	        const updateStatement = buildOfflineItemSyncStatusUpdateStatement(tempId, syncStatus, syncError, realId, this.getEnvironmentKey());

        tx.executeSql(
          updateStatement.sql,
          updateStatement.params,
          () => {
            resolve();
          },
          (_: Transaction, error: SQLError) => {
            console.error('[OfflineDB] Error updating offline item sync status:', {
              tempId,
              syncStatus,
              sql: updateStatement.sql,
              params: updateStatement.params,
              error: error.message,
              errorCode: error.code
            });
            reject(new Error(`Failed to update offline item sync status: ${error.message}`));
            return false;
          }
        );
      });
    });
  }

  /**
   * Delete offline on-demand item
   */
  async deleteOfflineOnDemandItem(itemIdOrTempId: number | string): Promise<void> {
    await this.initialize();

    if (!this.db) {
      throw new Error('Database not initialized');
    }

    try {
      // Delete by id, temp_id, or synced_* pattern to handle all cases
      // This ensures synced items cached from API are properly deleted
	      await this.executeSql(
	        'DELETE FROM offline_on_demand_items WHERE (id = ? OR temp_id = ? OR temp_id = ?) AND environment_key = ?',
	        [itemIdOrTempId, itemIdOrTempId, `synced_${itemIdOrTempId}`, this.getEnvironmentKey()]
	      );
    } catch (error) {
      console.error('[OfflineDB] ❌ Error deleting offline on-demand item:', error);
      throw new Error(`Failed to delete offline on-demand item: ${error instanceof Error ? error.message : 'Unknown'}`);
    }
  }

  /**
   * Update offline on-demand item
   */
  async updateOfflineOnDemandItem(
    itemId: number | string,
    updates: {
      name?: string;
      description?: string;
      category_id?: number;
      questions_schema?: any;
    }
  ): Promise<void> {
    if (!this.db) {
      throw new Error('Database not initialized');
    }

    const lookupId = await this.resolveOfflineOnDemandItemLookupId(itemId);

    return new Promise((resolve, reject) => {
      this.db!.transaction((tx: Transaction) => {
	        const updateStatement = buildOfflineOnDemandItemUpdateStatement(lookupId, updates, this.getEnvironmentKey());

        tx.executeSql(
          updateStatement.sql,
          updateStatement.params,
          () => {
            resolve();
          },
          (_: Transaction, error: SQLError) => {
            console.error('[OfflineDB] ❌ Error updating offline on-demand item:', error);
            reject(new Error(`Failed to update offline on-demand item: ${error.message}`));
            return false;
          }
        );
      });
    });
  }

  /**
   * Get all pending offline rooms
   */
  async getPendingOfflineRooms(): Promise<any[]> {
    if (!this.db) {
      throw new Error('Database not initialized');
    }

	    const selectStatement = buildPendingOfflineRoomsForEnvironmentSelectStatement(this.getEnvironmentKey());

    return new Promise((resolve, reject) => {
      this.db!.transaction((tx: Transaction) => {
        tx.executeSql(
          selectStatement.sql,
          selectStatement.params,
          (_: Transaction, result: ResultSet) => {
            resolve(resultRowsToArray(result));
          },
          (_: Transaction, error: SQLError) => {
            console.warn('[OfflineDB] Error getting pending offline rooms:', error);
            reject(new Error(`Failed to get pending offline rooms: ${error.message}`));
            return false;
          }
        );
      });
    });
  }

  /**
   * Get all pending offline on-demand items
   */
  async getPendingOfflineOnDemandItems(): Promise<any[]> {
    if (!this.db) {
      throw new Error('Database not initialized');
    }

	    const selectStatement = buildPendingOfflineOnDemandItemsForEnvironmentSelectStatement(this.getEnvironmentKey());

    return new Promise((resolve, reject) => {
      this.db!.transaction((tx: Transaction) => {
        tx.executeSql(
          selectStatement.sql,
          selectStatement.params,
          (_: Transaction, result: ResultSet) => {
            resolve(mapResultRows(result, (row) => mapRowToOfflineOnDemandItem(row, {
              onQuestionsSchemaParseError: (error) => {
                console.warn('[OfflineDB] Error parsing questions_schema:', error);
              },
            })));
          },
          (_: Transaction, error: SQLError) => {
            console.warn('[OfflineDB] Error getting pending offline items:', error);
            reject(new Error(`Failed to get pending offline items: ${error.message}`));
            return false;
          }
        );
      });
    });
  }

  // ==================== SHARED ITEM LINKS SUPPORT ====================

  /**
   * Cache shared item links from template data
   * Uses INSERT OR REPLACE to handle updates
   */
  async cacheSharedItemLinks(sharingLinks: any[]): Promise<void> {
    if (!this.db) {
      throw new Error('Database not initialized');
    }

    if (!sharingLinks || sharingLinks.length === 0) {
      return;
    }

    return new Promise((resolve, reject) => {
      this.db!.transaction(
        (tx: Transaction) => {
          for (const link of sharingLinks) {
            const statement = buildSharedItemLinkCacheStatement(link);
            tx.executeSql(statement.sql, statement.params);
          }
        },
        (error: SQLError) => {
          console.warn('[OfflineDB] Error caching shared item links:', error);
          reject(new Error(`Failed to cache shared item links: ${error.message}`));
        },
        () => {
          resolve();
        }
      );
    });
  }

  /**
   * Cache question mappings for shared item links
   */
  async cacheQuestionMappings(sharingLinks: any[]): Promise<void> {
    if (!this.db) {
      throw new Error('Database not initialized');
    }

    if (!sharingLinks || sharingLinks.length === 0) {
      return;
    }

    return new Promise((resolve, reject) => {
      this.db!.transaction(
        (tx: Transaction) => {
          for (const link of sharingLinks) {
            for (const statement of buildSharedQuestionMappingCacheStatements(link)) {
              tx.executeSql(statement.sql, statement.params);
            }
          }
        },
        (error: SQLError) => {
          console.warn('[OfflineDB] Error caching question mappings:', error);
          reject(new Error(`Failed to cache question mappings: ${error.message}`));
        },
        () => {
          resolve();
        }
      );
    });
  }

  /**
   * Get shared item links for a specific item (as source OR target)
   */
  async getSharedLinksForItem(itemId: number): Promise<any[]> {
    if (!this.db) {
      throw new Error('Database not initialized');
    }

    return new Promise((resolve, reject) => {
      this.db!.transaction((tx: Transaction) => {
        const statement = buildSharedLinksForItemSelectStatement(itemId);

        tx.executeSql(
          statement.sql,
          statement.params,
          (_: Transaction, result: ResultSet) => {
            resolve(mapResultRows(result, mapRowToSharedItemLink));
          },
          (_: Transaction, error: SQLError) => {
            console.warn('[OfflineDB] Error getting shared links for item:', error);
            reject(new Error(`Failed to get shared links: ${error.message}`));
            return false;
          }
        );
      });
    });
  }

  /**
   * Get all sharing links for multiple items in one batch query
   * More efficient than calling getSharedLinksForItem() for each item individually
   *
   * @param itemIds - Array of item IDs to query
   * @returns Map of itemId -> sharing links array
   */
  async getAllSharedLinksForItems(itemIds: number[]): Promise<Map<number, any[]>> {
    await this.initialize();

    if (!this.db || itemIds.length === 0) {
      return new Map(); // Guard against empty list
    }

    try {
      const linksByItem = new Map<number, any[]>();
      const CHUNK_SIZE = 450; // SQLite has ~999 variable limit, use 450 for safety (2x for source+target)

      // Process in chunks to avoid SQLite variable limit
      for (let i = 0; i < itemIds.length; i += CHUNK_SIZE) {
        const chunk = itemIds.slice(i, i + CHUNK_SIZE);
        const statement = buildSharedLinksForItemsSelectStatement(chunk);

        await new Promise<void>((resolve, reject) => {
          this.db!.transaction((tx: Transaction) => {
            tx.executeSql(
              statement.sql,
              statement.params,
              (_: Transaction, result: ResultSet) => {
                // Group results by item ID
                for (let j = 0; j < result.rows.length; j++) {
                  const link = result.rows.item(j);
                  const linkObj = mapRowToSharedItemLink(link);

                  // Add to source item's list
                  if (!linksByItem.has(link.source_item_id)) {
                    linksByItem.set(link.source_item_id, []);
                  }
                  linksByItem.get(link.source_item_id)!.push(linkObj);

                  // Add to target item's list
                  if (!linksByItem.has(link.target_item_id)) {
                    linksByItem.set(link.target_item_id, []);
                  }
                  linksByItem.get(link.target_item_id)!.push(linkObj);
                }
                resolve();
              },
              (_: Transaction, error: SQLError) => {
                console.warn('[OfflineDB] Error in batch sharing links query:', error);
                reject(new Error(`Failed to batch query sharing links: ${error.message}`));
                return false;
              }
            );
          });
        });
      }

      return linksByItem;
    } catch (error) {
      console.warn('[OfflineDB] Error in getAllSharedLinksForItems:', error);
      return new Map(); // Gracefully return empty map on error
    }
  }

  /**
   * Get question mappings for a specific shared item link
   */
  async getQuestionMappingsForLink(linkId: number): Promise<any[]> {
    if (!this.db) {
      throw new Error('Database not initialized');
    }

    return new Promise((resolve, reject) => {
      this.db!.transaction((tx: Transaction) => {
        const statement = buildQuestionMappingsForLinkSelectStatement(linkId);

        tx.executeSql(
          statement.sql,
          statement.params,
          (_: Transaction, result: ResultSet) => {
            resolve(mapResultRows(result, mapRowToQuestionMapping));
          },
          (_: Transaction, error: SQLError) => {
            console.warn('[OfflineDB] Error getting question mappings:', error);
            reject(new Error(`Failed to get question mappings: ${error.message}`));
            return false;
          }
        );
      });
    });
  }

  /**
   * Get completed responses for a specific property and item
   * Used for pre-filling shared items offline
   */
  async getCompletedResponsesForItem(propertyId: number, itemId: number): Promise<any[]> {
    if (!this.db) {
      throw new Error('Database not initialized');
    }

    return new Promise((resolve, reject) => {
      this.db!.transaction((tx: Transaction) => {
        const statement = buildCompletedResponsesForItemSelectStatement(propertyId, itemId);

        tx.executeSql(
          statement.sql,
          statement.params,
          (_: Transaction, result: ResultSet) => {
            resolve(mapResultRows(result, mapRowToCompletedResponse));
          },
          (_: Transaction, error: SQLError) => {
            console.warn('[OfflineDB] Error getting completed responses:', error);
            reject(new Error(`Failed to get completed responses: ${error.message}`));
            return false;
          }
        );
      });
    });
  }
}

// Export singleton instance
export const offlineDatabase = new OfflineDatabaseService();
